// COP — WebGL2 RenderBackend (deck.gl v9 MapboxOverlay over MapLibre v5).
//
// This is the concrete backend the render-engine talks to through its
// `RenderBackend` seam (packages/render-engine/src/backend.ts). The engine
// stays renderer-agnostic; all deck.gl / MapLibre specifics live here so the
// React shell never enters the render loop. We construct a single
// MapboxOverlay (interleaved) and rebuild deck layers from the per-frame
// coalesced entity map the engine hands us in writeFrame().

import { MapboxOverlay } from "@deck.gl/mapbox";
import { ScatterplotLayer, LineLayer, PathLayer, IconLayer, TextLayer } from "@deck.gl/layers";
import type { Layer } from "@deck.gl/core";
import type { Map as MapLibreMap, IControl } from "maplibre-gl";

import {
  symbolIcon,
  cotTypeToSidc,
  coalitionIcon,
  alertIcon,
  dotDiamondIcon,
  loadMilsymbol,
  milsymbolReady,
  normAffil,
  normDomain,
  type SymbolColors,
} from "./symbology.ts";
import { GaussianCloudLayer } from "./cloudLayer.ts";
import { crowdPipSprite } from "./livingNet.ts";
import { tuning } from "./renderTuning.ts";
import {
  type RenderSettings,
  type RenderSettingsInput,
  DEFAULT_RENDER_SETTINGS,
  resolveRenderSettings,
} from "./renderSettingsKeys.ts";

import type { RenderBackend } from "../../../../packages/render-engine/src/backend.ts";
import { ellipsePlan } from "../../../../packages/render-engine/src/ellipse.ts";
import { declutterPlan } from "../../../../packages/render-engine/src/declutter.ts";
import {
  deconflictLabels,
  fastDeconflictLabels,
  latchClusters,
  claimLabelSlot,
  FAST_DECONFLICT,
  type LabelClaims,
  type LabelInput,
  type LabelPlacement,
  type PlatePad,
} from "../../../../packages/render-engine/src/labelDeconflict.ts";
import {
  globalRank,
  globalRankIcon,
  densityLodMul,
  nameFallbackOpens,
  labelZoomGateOpen,
} from "../../../../packages/render-engine/src/globalRank.ts";
import { smearOpacityMultipliers } from "../../../../packages/render-engine/src/smearOverlap.ts";
import { haloOpacityMultipliers, type SmearDisc } from "../../../../packages/render-engine/src/haloOverlap.ts";
import {
  isFiniteLngLat,
  segmentNm,
  continuousRun,
  leaderLengthNm,
  clampAltM,
  MAX_RENDER_SEG_NM,
} from "../../../../packages/render-engine/src/geometryGuards.ts";
import {
  appendTrailPoint,
  type TrailPoint,
} from "../../../../packages/render-engine/src/trailPolicy.ts";
import { hypersonicBlipPhase } from "../../../../packages/motion-model/src/motion.ts";
import type {
  CanonicalEnvelope,
  LayerSpec,
  ViewState,
} from "../../../../packages/contracts-ts/src/index.ts";
import type { Affiliation } from "../../../../packages/schema/ts/envelope.ts";

/** RGBA color resolved from the affiliation legend; deck wants [r,g,b,a] 0-255. */
type RGBA = [number, number, number, number];

/** Hex (#RRGGBB) → deck RGBA. Falls back to a neutral grey on bad input. */
function hexToRgba(hex: string, alpha = 255): RGBA {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [156, 163, 175, alpha];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255, alpha];
}

/** Extract a [lng, lat] from a Point envelope; null for non-point geometry OR a NON-FINITE
 *  coordinate. The finite guard (isFiniteLngLat) is load-bearing: `typeof NaN === "number"` is
 *  TRUE, so the old `typeof c[0] === "number"` check passed a NaN/Infinity coordinate straight
 *  through to every LineLayer/PathLayer/marker — and a single non-finite endpoint draws a "laser"
 *  across the whole viewport. Returning null here drops the bad track from the frame entirely
 *  (rebuildDeck filters on pointOf !== null), failing safe instead of streaking the scope. */
function pointOf(env: CanonicalEnvelope): [number, number] | null {
  const g = env.geometry;
  if (!g || g.type !== "Point") return null;
  const c = g.coordinates;
  if (Array.isArray(c) && isFiniteLngLat(c as number[])) {
    return [c[0] as number, c[1] as number];
  }
  return null;
}

/** 2.5D-DEPTH: altitude (metres HAE, clamped >=0 so a slightly-negative live ADS-B reading near
 *  sea level surfaces cleanly instead of sinking the glyph below the deck without a stalk) — from
 *  properties.altitude_m (canonical, ADS-B-consistent) or the 3rd GeoJSON coordinate; 0 otherwise. */
function zOf(env: CanonicalEnvelope): number {
  // clampAltM: [0, MAX_ALT_M] with non-finite → 0. The UPPER clamp is the drop-line-stalk guard —
  // a garbage / out-of-range altitude can never lift a glyph (or shoot a stalk) to a screen-tall Z.
  const v = (env.properties as Record<string, unknown>).altitude_m;
  if (typeof v === "number" && Number.isFinite(v)) return clampAltM(v);
  const g = env.geometry;
  if (g && g.type === "Point") {
    const c = (g as { coordinates?: unknown }).coordinates;
    if (Array.isArray(c) && typeof c[2] === "number" && Number.isFinite(c[2])) return clampAltM(c[2]);
  }
  return 0;
}

/** [lng, lat, altMetres] for a Point envelope — the 3-tuple deck.gl reads as an
 *  absolute-altitude position. null for non-point geometry. */
function point3Of(env: CanonicalEnvelope): [number, number, number] | null {
  const p = pointOf(env);
  return p ? [p[0], p[1], zOf(env)] : null;
}

/** Altitude domain ceiling (metres) and the perceptually-weighted [0..1] norm — gamma
 *  < 1 gives more resolution down low where most traffic separates. */
const ALT_DOMAIN_M = 14_000;
function altNorm(z: number): number {
  return Math.pow(Math.max(0, Math.min(1, z / ALT_DOMAIN_M)), 0.7);
}

/** 2D-legible altitude tint: lighten + desaturate an affiliation colour by altitude.
 *  Both ops are achromatic (desaturate toward luma, lift toward white) so it survives the
 *  MONO/NVG profiles, and it writes RGB ONLY — alpha (staleness) is never touched. */
function altTint([r, g, b, a]: RGBA, z: number): RGBA {
  const t = altNorm(z);
  if (t <= 0) return [r, g, b, a];
  const chroma = 1 - 0.6 * t; // 1.0 -> 0.4 (desaturate toward grey)
  const lift = 0.28 * t; // 0 -> 0.28 toward white
  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  const mix = (c: number) => {
    const d = luma + (c - luma) * chroma;
    return Math.round(d + (255 - d) * lift);
  };
  return [mix(r), mix(g), mix(b), a];
}

/** 2.5D-DEPTH: depth params that keep a lifted screen-space overlay compositing in submission
 *  (painter's) order instead of depth-sorting against the basemap / each other once glyphs carry
 *  real Z at tilt — the same opt-out the smear uses (depthCompare 'always' = always draw;
 *  depthWriteEnabled false = never occlude a later layer). Preserves the back-to-front stack.
 *  (When P4 terrain lands, the stalk will want a real depth test to read behind hills.) */
const FLAT_OVERLAY_DEPTH = { depthCompare: "always", depthWriteEnabled: false } as const;

/** The backend needs affiliation colors; the host injects a resolver bound to
 *  the active design-tokens theme so symbology stays single-sourced. The optional
 *  `labelPlate` rider is the per-theme PROTECTIVE LABEL PLATE colour (the shipped themes'
 *  `label-plate` token, resolved by theme.ts affiliationColorsFor) — carried on this same
 *  seam so a runtime theme swap re-points the plate exactly like the palette
 *  (cop.ts setRenderProfile → setAffiliationColors → rebuild). Absent (external/coalition
 *  skin without the token) ⇒ DEFAULT_LABEL_PLATE, the near-black dark-family plate. */
export type AffiliationColors = Record<Affiliation, string> & { labelPlate?: string };

/** Fallback LABEL PLATE colour — the canon near-black rgb(6,9,14) the SDF label outline
 *  already uses, correct over the dark-matter basemap every dark-family theme rides. */
const DEFAULT_LABEL_PLATE = "#06090E";

/** Which marker carrier the COP renders affiliation with:
 *   - "milsym"    : MIL-STD-2525 / APP-6 frame SHAPE (symbology.ts) — affiliation is
 *                   carried by the frame geometry; hue only CONFIRMS (DESIGN-SPEC §2.3,
 *                   the MONO master test). This is the DEFAULT carrier ("Standard").
 *   - "dot"       : the legacy ScatterplotLayer marker (affiliation carried by COLOUR);
 *                   the clean low-ink "Minimal" carrier — best at density.
 *   - "coalition" : FULL MIL-STD-2525 symbols via milsymbol ("Coalition"), SIDC-derived
 *                   from the CoT type (cotTypeToSidc). The milsymbol chunk is lazy
 *                   (dynamic import); until it arrives — and for any track a SIDC cannot
 *                   be derived for — the milsym sprite renders instead (never blank). */
export type MarkerMode = "milsym" | "dot" | "coalition";

/** Default marker carrier. milsym makes affiliation survive a hue strip — it is the
 *  channel-law-correct default (shape carries, colour confirms). */
const DEFAULT_MARKER_MODE: MarkerMode = "milsym";

export interface DeckBackendOptions {
  map: MapLibreMap;
  affiliationColors: AffiliationColors;
  /** Optional radius (meters) for track points. */
  pointRadiusMeters?: number;
  /** Called on a deck pick so the shell can update the HUD selection. */
  onPick?: (env: CanonicalEnvelope | null) => void;
  /** Initial marker carrier (default "milsym"). */
  markerMode?: MarkerMode;
  /** Initial render settings (fades / ellipse / vector scale / drop-lines). Bundle A reads these
   *  with fallback constants — absent ⇒ DEFAULT_RENDER_SETTINGS, so the host is never blocked on
   *  Bundle B's store. The host re-pushes via setRenderSettings() when the settings change. */
  renderSettings?: RenderSettingsInput;
}

const FALLBACK_COLOR = "#9ca3af";

/** Achromatic scaffolding colour — a neutral cool-grey, identical under MONO/NVG.
 *  Used by the 2.5D stalk (the vertical drop-line) so it never reads as a second
 *  affiliation track. NOTE: the SMEAR cloud body NO LONGER uses this — the smear is
 *  now tinted per-track with the affiliation hue (see buildSmear getFillColor), so
 *  a hostile track's uncertainty reads red, a friend's green, matching its frame. */
const SCAFFOLD_TINT: RGBA = [150, 178, 205, 255];

// ── LIVE INTERPOLATION (C) — GPU TRANSITIONS ─────────────────────────────────────────────────
// deck.gl interpolates each track's POSITION on the GPU between data updates (transitions:
// {getPosition}), so the discrete feed (frontgate ~4s, density/ADS-B ~1s) renders as buttery as
// the per-tick mock WITHOUT a per-frame CPU rebuild. The expensive rebuild (declutter + halo-
// detune + label-deconflict + layer construction) fires ONLY on DATA/zoom/settings change; a
// cheap repaint PUMP advances the in-flight GPU transition between rebuilds. Dropping the 30fps
// rAF rebuild is what lets the picture scale to 100k dots — the screen-space passes stop running
// 30×/second and instead run once per ~1s data update. Glide duration = the measured feed
// interval, clamped so a fast feed can't snap-storm and a paused feed can't drift forever.
const TRANSITION_MIN_MS = 250;
const TRANSITION_MAX_MS = 4500;
const TRANSITION_DEFAULT_MS = 1500; // first frame (no measured interval yet)

/** Heuristic: does this string look like a raw machine UID rather than a human callsign?
 *  Used to keep the callsign-label fallback (properties.name) from rendering CoT UIDs as noise.
 *  Matches ATAK android ids, GUIDs, and long hex/opaque runs; a short tactical callsign passes. */
function looksLikeUid(s: string): boolean {
  return (
    /^android-/i.test(s) || // ATAK: "ANDROID-004badc0ffee..."
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}/i.test(s) || // GUID/UUID
    /^[0-9a-f]{12,}$/i.test(s) || // long bare hex
    /^[A-Za-z]+-[0-9a-f]{10,}$/i.test(s) // prefix-hex opaque ids
  );
}
// SCALE CAPS — above these counts the screen-space passes are SKIPPED. They are near-O(N²) in a
// dense cluster (bench: halo-detune ~54ms@20k / 314ms@50k; label-deconflict ~294ms@1k / 2.7s@2k),
// and at that density the green-wash / label legibility they protect is moot anyway (the markers
// merge into a field; 250+ overlapping labels are unreadable). Skipping fails SAFE — a halo with
// no detune is multiplier 1 (full, never brighter), a label with no deconfliction sits at its base
// offset. PRESENTATION-ONLY (I13) either way: WHAT renders is unchanged.
// OPERATOR-TUNABLE: tuning.haloDetuneMaxEntities (10k). DENSE MODE — above
// tuning.decorationMaxEntities (12k) the per-rebuild DECORATION passes (motion trails, future
// paths, velocity leaders, drop-lines, blips, crowd-pips, halo) — each O(total) to construct — are
// the rebuild bottleneck AND read as noise at that density. Above it we render JUST the markers
// (the dots ARE the picture at 100k) + the focus smear; the decorations return at lower density /
// once a filter thins the set. Markers stay on the GPU (deck draws 100k points comfortably).
// Both live in render-tuning.json (renderTuning.ts) — edit + reload, no rebuild.
// Above this many ON-SCREEN labels the EXACT (gradient + shrink) deconfliction is too slow
// (near-O(N²)), so we fall back to fastDeconflictLabels — an O(N) grid with priority order, a name
// cap, and drop-at-distance (a dense cluster stays readable-enough to SEE the cluster; zoom in for
// detail). Below it, the prettier exact pass runs.
const LABEL_DECONFLICT_EXACT_MAX = 300;
// PLACEMENT PRIORITY — ONE global doctrine-preserving ordinal for labels AND icons: globalRank
// (packages/render-engine/src/globalRank.ts; DESIGN-CLUTTER-PRIORITY v2 §1.1). Labels rank
// emphasized 0 → alert 1 → friend 2 (own force FIRST — Will's shipped placement doctrine) →
// hostile 3 → unknown/pending 4 → neutral bulk 5, and the rank feeds every label surface (the
// stable-placement sort, the density cull, the latch stack order, the fast-grid slot priority).
// Icons use the emphasis-free variant (globalRankIcon, 1..5) so the density LOD stays
// hover-independent. The old per-surface AFFILIATION_LABEL_RANK map is gone — one ordinal,
// one story, T0-pinned in render-engine.

/** Resolved screen-space placement for ONE callsign label (deconflictCallsigns output; the
 *  buildCallsigns TextLayer accessors read it per datum). offsetX/anchor deviate from the
 *  {0,'middle'} defaults ONLY for CLUSTER-LATCHED labels (the left-justified stack beside a
 *  dense knot); every flow label keeps the classic Y-only, centred-under-the-dot law. */
type CallsignPlace = {
  offsetX: number;
  offsetY: number;
  size: number;
  anchor: "start" | "middle" | "end";
  /** Final anchor screen-X (px) — the DRAW-ORDER key: buildCallsigns sorts ascending so the
   *  RIGHT-most label/column draws LAST and paints ON TOP of anything to its left (Will's
   *  right-over-left rule for overlapping adjacent latch columns). */
  sortX: number;
  /** ROW-CAP CUE: this placement is a truncated latch column's "…" carrier — getText renders
   *  "…" here, not the callsign. Lives on the PER-SPEC place record (not a backend-global) so a
   *  later spec's rebuild can't clobber an earlier spec's carriers before deck evaluates getText. */
  ellipsis?: boolean;
};

/** DOT-MODE HOSTILE DIAMOND size multiplier — the rendered tip-to-tip extent relative to the
 *  equivalent circle's DIAMETER. A diamond inscribed at 1.0 reads visually lighter than the
 *  circle it replaces (area 2d² vs πr²); ×1.3 restores near-equal visual weight
 *  (2·(1.3r)² ≈ 3.38r² ≈ πr²). Shared by buildDotDiamonds (getSize) and labelBelowPx (the
 *  label offset must clear the DRAWN extent). */
const DOT_DIAMOND_MUL = 1.3;
/** Breathing gap (px) between a marker's drawn edge and its callsign label's top edge (the
 *  plate's backgroundPadding eats ~2px of it). Rides on labelBelowPx's per-entity half-extent. */
const LABEL_GAP_PX = 6;
/** Gap (px) between a latched cluster's edge marker and the label stack's shared edge. */
const LATCH_MARGIN_PX = 10;
/** Extra row pitch (px) on top of the base font size for a latched stack (line height). */
const LATCH_LINE_GAP_PX = 3;
/** Label-plate padding [x, y] px around the glyph box (deck TextLayer backgroundPadding). */
const PLATE_PADDING: [number, number] = [4, 2];
/** performance.now() with a Date.now() fallback (headless/SSR). The pump clock. */
const perfNow = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now());

/** IVM-3: a single shared empty frame array. Using ONE stable ref for absent specs keeps their
 *  cached filtered subset reference-stable across rebuilds too (a bare `[]` would allocate a fresh
 *  ref each call and defeat the cache). Never mutated. */
const EMPTY_FRAME: CanonicalEnvelope[] = [];

// LOOK-AND-FEEL + LABEL-LOD KNOBS — ALL operator-tunable via render-tuning.json (renderTuning.ts;
// edit + reload, no rebuild). Live-read at use sites, so keep reading through `tuning.`:
//   tuning.trailLen            (48)  — positions kept per track for the comet-tail motion trail
//   tuning.trailMaxGapS        (30)  — TIME-GAP trail break: a track untouched by writeFrame for
//     longer than this (hidden/throttled tab) restarts its trail — no segment across the gap
//   tuning.trailMaxAgeS        (300) — trail-point max age: pruned from the buffer AND the render
//     fades each segment toward zero as it approaches this age (contrail dissipation)
//   tuning.trailLongSegNm      (8)   — a segment longer than this renders at TAIL styling, never
//     head — defense-in-depth against throttled-rAF outsized steps reading as a flight path
//   tuning.trailHeadPx         (2.0) — widest (head) trail segment width, px
//   tuning.markerPx            (5.5) — base marker radius in SCREEN px (zoom-independent)
//   tuning.haloPx              (13)  — soft glow radius behind each marker
//   tuning.callsignBaseSizePx  (11)  — base callsign font size (px) before declutter shrink
//   tuning.minLabelPx          (9)   — CALLSIGN LOD, "HIDE don't squeeze": a callsign the
//     deconfliction had to SHRINK below this many px is HIDDEN entirely rather than drawn
//     unreadable. Friendly + emphasized labels are shrink-exempt, so this thins only the
//     low-priority (neutral/unknown) pile — the "wall of tiny green callsigns".
//   tuning.labelDensityKeep    (120) — PRIORITY-TIERED DENSITY CULL: past this many on-screen
//     labels the lowest-priority tiers (by globalRank — neutral first) drop BEFORE
//     layout, so alerts + own-force + threats claim the room. Friendly + emphasized are NEVER culled.
//     Also bounds the near-O(N²) exact pass's input (clamped ≤400 in renderTuning for that reason).
//   tuning.sparseLabelShowAll  (24)  — SPARSE-VIEW "show all callsigns", the INVERSE of the
//     density cull: at most this many tracks in the viewport ⇒ the label gates RELAX (zoom gate
//     bypassed + name-fallback opens to every affiliation — room exists, so everything reads).
//     0 disables. Self-corrects next rebuild if the operator pans into a dense area.
//   tuning.labelNameMax (18) / tuning.sparseLabelNameMax (40) — TRUNCATION caps (never drop-gates)
//     for name-fallback labels in dense/sparse views; hover/tap reveals the full text.
//   tuning.labelClusterRadiusPx (26) — CLUSTER-LATCH grouping radius: surviving label anchors
//     within this screen distance (single-linkage) form one cluster for the latch pass.
//   tuning.labelClusterLatch   (5)  — CLUSTER-LATCH threshold: a group of at least this many
//     labels latches into ONE left-justified stacked column beside the group (full base size —
//     the latch IS the declutter). <2 disables (0 = the documented "off").
//   tuning.labelPlateAlpha     (0.55) — PROTECTIVE TEXT PLATE opacity behind every callsign
//     (theme-coloured backing; the primary legibility device). 0 disables the plate and
//     restores the full 2px SDF outline so legibility never regresses.

// PERF METER (dev/ops render-cadence readout) — measures the wall-clock interval between PAINTED deck
// frames (via deck's onAfterRender: feed glides AND user pan/zoom) and shows a smoothed FPS +
// frame-time in a tiny always-on corner overlay. Pure presentation (I13): it OBSERVES the render loop,
// never touches data / PDP / clearance. Cheap by construction — per-frame work is numeric only (EMA,
// no allocation); the DOM is repainted off a low-frequency timer (PERF_HUD_REFRESH_MS), never per
// painted frame (no React re-render storm).
const FPS_EMA_ALPHA = 0.1;        // smoothing weight for the frame-rate / frame-time EMA
const PERF_HUD_REFRESH_MS = 250;  // corner-overlay repaint cadence (~4 Hz) — decoupled from the paint loop
const FRAME_IDLE_MS = 400;        // no painted frame within this window ⇒ report "idle" (deck not repainting)
const MAX_FRAME_GAP_MS = 250;     // an inter-frame gap longer than this = deck resuming after a static-view
const MIN_FRAME_MS = 2;           // shorter than this = a sub-frame onAfterRender burst (deck can fire >1×/paint) — not a real frame interval; skip so fps isn't inflated to thousands
                                  // pause, not a slow frame — dropped rather than falsely tanking the average

// ===========================================================================
//  COMET-TAIL — the age-gradient history trail ramp (rank-1 visual upgrade).
//  Each track's recent positions render as a per-segment LineLayer whose ALPHA
//  and WIDTH decay into the PAST: a crisp, wide head (the just-now position)
//  fading to a faint, thin oldest tail. The ramp is keyed by "steps back from
//  the head" (0 = the segment ending at the live position) so the decay rate is
//  FIXED regardless of how full the history buffer is — a short young trail just
//  doesn't reach the faint end. Precomputed once (no per-frame lerp/pow).
// ===========================================================================
const TRAIL_HEAD_ALPHA = 200; // brightest (head) segment alpha
const TRAIL_TAIL_ALPHA = 12; // faintest (oldest) segment alpha
// Head segment WIDTH is operator-tunable (tuning.trailHeadPx, default 2.0 — the contrail feel);
// the tail width stays a fixed thin wisp.
const TRAIL_TAIL_PX = 0.8; // thinnest (oldest) segment width, px
/** alpha + width by steps-from-head, index [0..len-1]. MEMOIZED on the tunable trail length +
 *  head width (tuning.trailLen / tuning.trailHeadPx) — rebuilt only when a config value differs
 *  from the cached table, so the per-segment lookup stays an array index. lerp is a hoisted
 *  function declaration so it is callable here despite appearing later in the file. */
let trailRampCache: {
  len: number;
  headPx: number;
  ramp: ReadonlyArray<{ alpha: number; width: number }>;
} | null = null;
function trailRamp(len: number, headPx: number): ReadonlyArray<{ alpha: number; width: number }> {
  if (trailRampCache?.len !== len || trailRampCache?.headPx !== headPx) {
    const n = Math.max(1, Math.round(len));
    trailRampCache = {
      len,
      headPx,
      ramp: Array.from({ length: n }, (_unused, steps) => {
        const f = n > 1 ? 1 - steps / (n - 1) : 1; // 1 at head -> 0 at tail
        return {
          alpha: Math.round(lerp(TRAIL_TAIL_ALPHA, TRAIL_HEAD_ALPHA, f)),
          width: lerp(TRAIL_TAIL_PX, headPx, f),
        };
      }),
    };
  }
  return trailRampCache.ramp;
}

// ===========================================================================
//  FORWARD FADE — the dead-reckoned FUTURE-path ramp (mirror of the comet-tail,
//  pointing the other way). Each track's projection (properties.future_path,
//  up to 8 points at +30s..+240s) renders as per-segment items whose ALPHA and
//  WIDTH decay INTO THE FUTURE: a modest, wider near-marker head (t≈+30s) fading
//  to a faint, thin tip (t≈+240s). Keyed by "steps FORWARD from the head"
//  (0 = the segment leaving the live position) so the decay rate is FIXED no
//  matter how many projected points a track carries — a short/truncated forecast
//  just doesn't reach the faint tip. Precomputed once (no per-frame lerp).
//  Peak alpha stays a calm projection cue (~the old constant 50, a touch higher
//  at the head); the tip fades toward ~0 so a long fast forecast never draws a
//  solid spear across the scope.
// ===========================================================================
const FUTURE_LEN = 8; // max forward segments (live head + up to 8 projected +30s..+240s points)
const FUTURE_HEAD_ALPHA = 60; // near-marker (t≈+30s) segment alpha — a touch above the old constant 50
const FUTURE_TIP_ALPHA = 4; // far-forward (t≈+240s) tip alpha — near-0, a faint "less certain" whisper
const FUTURE_HEAD_PX = 1.5; // near-marker segment width, px (~the old constant 1.4)
const FUTURE_TIP_PX = 0.6; // far-forward tip width, px — tapers thin
/** alpha + width by steps-FORWARD-from-head, index [0..FUTURE_LEN-1]. */
const FUTURE_RAMP: ReadonlyArray<{ alpha: number; width: number }> = Array.from(
  { length: FUTURE_LEN },
  (_unused, steps) => {
    const f = FUTURE_LEN > 1 ? 1 - steps / (FUTURE_LEN - 1) : 1; // 1 at head -> 0 at tip
    return {
      alpha: Math.round(lerp(FUTURE_TIP_ALPHA, FUTURE_HEAD_ALPHA, f)),
      width: lerp(FUTURE_TIP_PX, FUTURE_HEAD_PX, f),
    };
  },
);

/** Desaturate an RGBA toward its luma by `sat` in [0,1] (1 = unchanged, 0 = grey).
 *  Achromatic mix so it survives the MONO/NVG profiles; alpha is preserved. Used by
 *  the dead-reckoned future path so a projection reads as a calmer, "less certain"
 *  echo of the affiliation hue without inventing a new colour. */
function desaturate([r, g, b, a]: RGBA, sat: number): RGBA {
  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  const mix = (c: number) => Math.round(luma + (c - luma) * sat);
  return [mix(r), mix(g), mix(b), a];
}

/** SHARED CROSS-LANE CONTRACT reader: properties.future_path — up to 8 forward
 *  positions [lon, lat, altMetres] from the deterministic motion model. ABSENT
 *  (undefined / not an array / empty) for records with no motion model (e.g. live
 *  ADS-B): returns null so the render lane draws nothing for that track. Each tuple
 *  is validated + the altitude clamped >=0 (mirrors zOf) so a malformed entry can
 *  never blank the interleaved render pass. */
function futurePathOf(env: CanonicalEnvelope): [number, number, number][] | null {
  const fp = (env.properties as Record<string, unknown>).future_path;
  if (!Array.isArray(fp) || fp.length === 0) return null;
  const out: [number, number, number][] = [];
  for (const pt of fp) {
    if (
      Array.isArray(pt) &&
      typeof pt[0] === "number" && Number.isFinite(pt[0]) &&
      typeof pt[1] === "number" && Number.isFinite(pt[1])
    ) {
      const alt = typeof pt[2] === "number" && Number.isFinite(pt[2]) ? Math.max(0, pt[2]) : 0;
      out.push([pt[0], pt[1], alt]);
    }
  }
  return out.length >= 1 ? out : null;
}

// ===========================================================================
//  SMEAR — the Gaussian uncertainty cloud tuning tokens + derivation.
//  The decay/reach curve is DATA, not magic numbers (mirrors the prototype's
//  TOKENS block). These are the ONLY knobs the smear reads.
// ===========================================================================
const SMEAR = {
  /** Cloud reach (geographic radius, METRES) at zero confidence — the widest a
   *  fully-uncertain track's cloud spreads. Reach shrinks toward fixRadiusM as
   *  confidence climbs. Pure function of CONFIDENCE (never age). */
  confReachM: 12_000,
  /** Tight-fix reach (metres) at full confidence — a crisp, small soft dot. */
  fixRadiusM: 700,
  /** Hard cap so a cloud never eats the theatre. */
  maxReachM: 22_000,
  /** Gaussian sigma (fraction of reach) at FULL confidence — crisp, tight core. */
  sigmaCrisp: 0.16,
  /** Gaussian sigma (fraction of reach) at ZERO confidence — wide, soft falloff. */
  sigmaSoft: 0.65,
  /** AT-REST WHISPER peak alpha (fresh track) — a faint ambient hint, NOT a wall of
   *  white. The resting map stays calm; uncertainty still reads as cloud WIDTH
   *  (reach/sigma = confidence), but the glow is barely-there until a track is
   *  emphasized (selected/hovered). Was 0.62 — dropped ~7x to whisper. The two
   *  channels are untouched: this scales alpha only. */
  peakAlphaCap: 0.09,
  /** BLOOM emphasis — when a track is SELECTED or HOVERED, ITS smear blooms so that
   *  one track's uncertainty reveals on focus. This is an INTERACTION-STATE overlay
   *  (like the marker size-bump / halo), NOT a data channel: it rides on alpha to
   *  spotlight the focused track, it does not re-encode age. */
  bloomPeakMult: 5.0,
  /** Absolute ceiling on a bloomed cloud's peak alpha so even a dense bloom stays a
   *  haze (a reveal, not the old wall of white). */
  bloomPeakCap: 0.5,
  /** A touch of extra reach on bloom so the focused cloud visibly breathes outward
   *  (emphasis only; the resting reach still = confidence). */
  bloomReachMult: 1.12,
  /** Age (ms past valid_time.stale) at which peak alpha reaches the floor. This
   *  spans the entity store's dim window so the fade tracks the engine's staleness
   *  ramp (fresh -> stale -> [expired/pruned by the store]). */
  stalenessRampMs: 60_000,
  /** Floor peak-alpha multiplier — a stale cloud dims but never fully vanishes
   *  (until the store expires the entity entirely). */
  opacityFloor: 0.08,
  /** Confidence used when a track carries no `properties.confidence`. */
  defaultConfidence: 0.5,
} as const;

// TRACKED-TASK:TODO.md#smear-motion-ellipse A MOVING track's uncertainty should be
// an ELLIPSE elongated along heading + scaled by speed (anisotropic Gaussian:
// sigma_x != sigma_y, rotated by heading_deg) — directional uncertainty. Today the
// smear is an ISOTROPIC radial Gaussian (one sigma). Needs a per-instance heading +
// aspect attribute in GaussianCloudLayer and a rotated unitPosition in the fragment.
// NOT built here; see TODO.md#smear-motion-ellipse.

/** The per-track smear parameters the shader reads. reach + sigma come from
 *  CONFIDENCE only; peakAlpha comes from AGE only — the two channels never cross
 *  (DESIGN-CONTEXT §2.2, the reach-bleed fix). */
interface SmearGeometry {
  /** Cloud geographic radius in METRES (CONFIDENCE only). */
  reachMeters: number;
  /** Gaussian width as a fraction of reach, [sigmaCrisp..sigmaSoft] (CONFIDENCE only). */
  sigma: number;
  /** Peak alpha multiplier in [0..peakAlphaCap] (AGE / STALENESS only). */
  peakAlpha: number;
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Derive the smear geometry for one envelope.
 *
 *  CONFIDENCE -> reach + sigma (the cloud's extent + edge-hardness). PURE function
 *  of `properties.confidence` (fallback ~0.5). There is NO age term in reach — that
 *  is the whole point of the channel-law fix: a wide cloud is wide because it IS
 *  uncertain (low confidence), not because it is old.
 *
 *  AGE (now - valid_time.stale) -> peakAlpha (and ONLY peakAlpha). A stale track's
 *  cloud is dimmer; freshness changes nothing but alpha. `nowMs` is the engine/wall
 *  clock the staleness ramp is measured against.
 */
function smearGeometry(env: CanonicalEnvelope, nowMs: number): SmearGeometry {
  // --- CONFIDENCE channel: reach (metres) + sigma (edge hardness). No age here. ---
  const rawConf = (env.properties as Record<string, unknown>).confidence;
  const conf =
    typeof rawConf === "number" && Number.isFinite(rawConf)
      ? clamp01(rawConf)
      : SMEAR.defaultConfidence;
  // High confidence -> reach collapses toward the tight fix radius; low confidence
  // -> reach opens toward confReachM. Linear in (1 - conf), then capped.
  const reachMeters = Math.min(
    SMEAR.maxReachM,
    lerp(SMEAR.fixRadiusM, SMEAR.confReachM, 1 - conf),
  );
  // High confidence -> crisp tight Gaussian; low confidence -> wide soft falloff.
  const sigma = lerp(SMEAR.sigmaCrisp, SMEAR.sigmaSoft, 1 - conf);

  // --- STALENESS channel: peakAlpha (and ONLY peakAlpha). No confidence here. ---
  // age is measured PAST valid_time.stale: a still-fresh track sits at full peak,
  // then fades to the floor across stalenessRampMs (the store's dim window). This
  // matches the engine's fresh -> stale ramp; the store expires/prunes past it.
  const staleAt = Date.parse(env.valid_time.stale);
  const ageMs = Number.isNaN(staleAt) ? 0 : Math.max(0, nowMs - staleAt);
  const ageNorm = clamp01(ageMs / SMEAR.stalenessRampMs);
  const peakAlpha = SMEAR.peakAlphaCap * lerp(1.0, SMEAR.opacityFloor, ageNorm);

  return { reachMeters, sigma, peakAlpha };
}

/** Kinds that get an uncertainty smear: live positional beliefs (tracks) and the
 *  observations behind them. Static geometry / messages / tasks do not. */
function hasSmear(env: CanonicalEnvelope): boolean {
  return env.kind === "track" || env.kind === "observation";
}

/** Read a numeric property (heading_deg / speed_kn) or null. */
function numProp(env: CanonicalEnvelope, key: string): number | null {
  const v = (env.properties as Record<string, unknown>)[key];
  return typeof v === "number" ? v : null;
}

/** Project [lon,lat] forward along a heading to draw a velocity leader line.
 *  Length scales with speed (~2 minutes ahead), clamped so it always reads, then multiplied by
 *  the user `scale` (advanced.vectorScale — Bundle B). The clamp is applied to the BASE length so
 *  vectorScale can deliberately push past the resting 36nm ceiling when the operator wants longer
 *  vectors. scale 1 ⇒ the legacy behaviour exactly. */
function leaderTarget(
  lon: number,
  lat: number,
  headingDeg: number,
  speedKn: number,
  scale = 1,
): [number, number] {
  // FAIL-SAFE: a non-finite position / heading would otherwise project the leader to a garbage
  // point (a screen-spanning line). Return the origin ⇒ a zero-length, invisible leader.
  if (!Number.isFinite(lon) || !Number.isFinite(lat) || !Number.isFinite(headingDeg)) {
    return [lon, lat];
  }
  // leaderLengthNm is the legacy base (speed → [1.5,36]nm × vectorScale) HARD-CAPPED at an
  // absolute ceiling, so a large vectorScale can never draw a screen-spanning streak. scale 1 ⇒
  // the legacy length exactly (the ~36nm resting cap holds).
  const nm = leaderLengthNm(speedKn, scale);
  const rad = (headingDeg * Math.PI) / 180;
  const dLat = (nm / 60) * Math.cos(rad);
  const dLon = (nm / 60) * Math.sin(rad) / Math.cos((lat * Math.PI) / 180);
  return [lon + dLon, lat + dLat];
}

/** Hypersonic detection: a track moving at/above this speed (knots) is treated as a hypersonic
 *  blip (the periodic pulse, cadence ∝ speed). ~Mach 2.3 at altitude — well above any conventional
 *  air track in the corpus, so only genuine hypersonics trip it. */
const HYPERSONIC_MIN_KN = 1500;

/** Per-affiliation domain-stretch lookup for the velocity-correlated ellipse: which advanced-tab
 *  ellipse knob governs a track's along-track elongation. maritime → tight (shipSigma); unknown/
 *  hostile air → wide (unknownAirSigma); other air/uas → airSigma; ground/other → tight. */
function ellipseStretchFor(env: CanonicalEnvelope, s: RenderSettings): number {
  const p = env.properties as Record<string, unknown>;
  const dom = typeof p.domain === "string" ? p.domain.toLowerCase() : "";
  const aff = env.properties.affiliation;
  const air = dom === "air" || dom === "uas" || dom === "uav";
  if (air) {
    return aff === "unknown" || aff === "hostile"
      ? s.advanced.ellipse.unknownAirSigma
      : s.advanced.ellipse.airSigma;
  }
  // maritime / ground / other → the tight ship assumption (slow movers barely elongate anyway).
  return s.advanced.ellipse.shipSigma;
}

/** A deck.gl-backed RenderBackend. Built by the host once the map canvas exists. */
export class DeckBackend implements RenderBackend {
  private readonly map: MapLibreMap;
  private readonly overlay: MapboxOverlay;
  /** Mutable so a runtime theme swap can re-point the affiliation palette without
   *  rebuilding the backend (drives both the dot fill and the milsym frame stroke). */
  private affiliationColors: AffiliationColors;
  private readonly pointRadiusMeters: number;
  private readonly onPick?: (env: CanonicalEnvelope | null) => void;
  /** Active marker carrier (milsym frame-shape vs legacy dot). */
  private markerMode: MarkerMode;
  /** Cheap palette fingerprint for deck updateTriggers — changes when a theme swap
   *  re-resolves the affiliation hexes, so getIcon re-runs and re-keys sprites. */
  private colorsKey = "";
  /** IVM-3 SYMBOLOGY trigger — a fingerprint of everything that recolours / re-opacities a
   *  marker WITHOUT the geometry changing: palette (colorsKey) + neutral/unknown fade + ellipse
   *  assumptions + drop-line opacities + vector scale. Changes ONLY on a theme / render-settings
   *  swap — NEVER on hover / select / pan — so a settings change re-resolves the colour accessors
   *  while an interaction leaves them (and the in-flight GPU glide) untouched. Recomputed once per
   *  rebuildDeck. */
  private symbologyKey = "";
  /** IVM-3 INTERACTION trigger — `${selectedId}|${hoveredId}|${declutterKey}`. Changes on a
   *  selection, a hover, or a zoom-declutter bucket flip. Gates the selection ring + the
   *  declutter-scaled marker sizes; DISJOINT from dataVersion so an interaction never re-runs the
   *  position accessor (which would restart the transition and jerk the dot). Recomputed once per
   *  rebuildDeck, after declutterKey. */
  private interactionKey = "";

  /** Registered layer specs by id (engine drives create/patch/destroy). */
  private readonly specs = new Map<string, LayerSpec>();
  /** Latest per-layer entity data from the engine's single frame write. */
  private frameData = new Map<string, CanonicalEnvelope[]>();
  /** Per-REBUILD registry of drawn label slots (text @ 24px-bucketed anchor) shared ACROSS
   *  every spec's ::callsign layer in one rebuildDeck pass (cleared at the top of the pass).
   *  The identical-text radius dedupe (v2 §1.4): the engine fans a kind-matching entity into
   *  MULTIPLE specs, and an egressed marker echoes back as a second same-name entity at a
   *  slightly DIFFERENT anchor — without this, each copy draws the SAME string and the
   *  deconflict passes fan them into a visible stacked pile (the "WP-2 ×3" bug; the
   *  "COALITION-BRIEF ×2" echo twin). See labelDeconflict.ts claimLabelSlot. */
  private readonly labelClaims: LabelClaims = new Map();
  /** IVM-3 STABLE-DATA cache — the renderable (finite-geometry) subset per spec, keyed by the
   *  identity of the raw frame array it was derived from. The engine hands a FRESH array every
   *  writeFrame (engine.tick maps a new array per frame), so raw-array identity is an exact
   *  "did the data change" signal: an interaction / settings / zoom rebuild reuses the SAME raw
   *  array ⇒ the SAME filtered ref, and deck.gl then honours updateTriggers instead of
   *  regenerating every attribute for all N. Invalidated automatically when writeFrame produces a
   *  new raw array; pruned on destroyLayer / dispose. */
  private readonly dataCache = new Map<
    string,
    {
      raw: CanonicalEnvelope[];
      filtered: CanonicalEnvelope[];
      /** `filtered` minus kind:"alert" — the marker layers' data (alerts wear the alert
       *  glyph instead of a unit frame; same ref as `filtered` when there are no alerts). */
      markers: CanonicalEnvelope[];
      /** The kind:"alert" subset of `filtered` — the ::alertglyph layer's data. */
      alerts: CanonicalEnvelope[];
      /** DOT-mode split of `markers`: non-hostile (the classic circle ScatterplotLayer)…
       *  (same ref as `markers` when there are no hostiles). */
      dotRounds: CanonicalEnvelope[];
      /** …and hostile-affiliation (the rotated-diamond ::dotdiamond IconLayer — the 2525
       *  hostile shape cue in Minimal mode). Empty ⇒ the shared EMPTY_FRAME ref. */
      dotDiamonds: CanonicalEnvelope[];
    }
  >();
  /** Index of id → envelope for synchronous picking. */
  private readonly byId = new Map<string, CanonicalEnvelope>();
  /** Per-entity position history for motion trails (most-recent last). Each sample carries the
   *  wall-clock ms it was recorded (TrailPoint) so the append policy can BREAK on a time gap
   *  (hidden-tab rAF pause) and the renderer can age-fade segments — see trailPolicy.ts. */
  private readonly trails = new Map<string, TrailPoint[]>();
  /** Currently-selected entity id (gets a selection ring + a bloomed smear). */
  private selectedId: string | null = null;
  /** Currently-hovered entity id (gets a bloomed smear on focus). Set by the host
   *  from a non-mutating pick under the cursor (see setHovered / pickId). */
  private hoveredId: string | null = null;
  /** Monotonic rebuild counter — the per-rebuild epoch. Still drives the DECORATION layers
   *  (trails / heading / future / drop-lines / blips / callsigns / smear), each of which builds a
   *  FRESH derived array every rebuild (so their triggers are structurally re-evaluated regardless)
   *  and the zoom-sensitive halo detune fill. It intentionally does NOT gate the three stable-data
   *  layers' geometry — those use dataVersion so an interaction never re-runs them. */
  private frameN = 0;
  /** IVM-3 DATA trigger — bumped ONCE per real data write (writeFrame), never on an interaction.
   *  Gates the position + data-derived accessors of the three stable-data layers (::dot, ::milsym,
   *  ::halo), so a hover / select / zoom / settings rebuild leaves getPosition alone and the
   *  in-flight GPU transition glides on uninterrupted (the interaction-jerk fix). */
  private dataVersion = 0;
  private lastFrameMsValue = 0;
  // ── LIVE INTERPOLATION (C) state ── GPU transitions own the per-track glide; a repaint pump
  //    advances the in-flight transition between data rebuilds. Pure presentation (I13): the GPU
  //    interpolates the RENDER position between feed updates — never WHAT renders, never the data/
  //    PDP/clearance. The pump parks (zero cost) whenever no glide is in flight.
  private transitionMs = TRANSITION_DEFAULT_MS; // current deck transition duration (measured feed interval)
  private transitionDeadline = 0;               // perfNow ms until which the pump keeps repainting
  private lastWriteMs = 0;                       // last writeFrame clock — measures the inter-update interval
  private pumpHandle: number | null = null;
  /** CHOPPY-BLIP FIX: the last full layer array handed to the overlay, so the per-frame blip
   *  refresh (refreshBlips) can swap ONLY the ::blip layers — every other layer INSTANCE is passed
   *  through untouched, so deck's prop-diff skips it (no marker/halo/label re-tessellation, and the
   *  in-flight position glide is NOT restarted). Empty until the first rebuild. */
  private currentLayers: Layer[] = [];
  /** CHOPPY-BLIP FIX: the (tiny — usually 0–2) hypersonic subset per spec, captured each rebuild.
   *  While non-empty the pump keeps running and re-samples the pulse envelope at FRAME rate, so the
   *  ring breathes smoothly instead of STEPPING at the ~1 Hz data cadence the GPU-transition pump
   *  alone advances (the pump only interpolates POSITION on the GPU; it never re-runs the blip
   *  accessor). Empty ⇒ the pump parks at rest exactly as before. */
  private blipInputs: Array<{ id: string; data: CanonicalEnvelope[] }> = [];
  // ── PERF METER state ── observes deck's onAfterRender to report render cadence. Presentation-only (I13).
  private frameIntervalEmaMs = 0;                // smoothed inter-frame interval (ms); fps = 1000/this at read time
  private lastFrameClock = 0;                    // perfNow() of the previous painted deck frame (0 = none yet)
  private perfHudEl: HTMLElement | null = null;  // the always-on corner overlay (null when suppressed / headless)
  private perfHudTimer: ReturnType<typeof setInterval> | null = null;
  /** Resolved render settings (fades / ellipse / vector scale / drop-lines). Bundle A reads
   *  these with fallback constants; the host re-pushes via setRenderSettings() on a B change. */
  private renderSettings: RenderSettings = DEFAULT_RENDER_SETTINGS;
  /** Per-rebuild zoom-declutter plan (marker scale / label gate / aggregate). Recomputed at the
   *  top of rebuildDeck from the live zoom + entity count. */
  private declutter = declutterPlan({ zoom: 9, entityCount: 0 });
  /** Quantized declutter fingerprint for deck updateTriggers (flips only across buckets).
   *  Parts: markerScale | labelsVisible | quantized densityFactor | blueForceAlwaysProminent. */
  private declutterKey = "1.00|0|0.00|1";
  /** ICON-DENSITY LOD driver — the quantized (0.05 steps) [0..1] density factor recomputed once
   *  per rebuild from the in-viewport icon count (tuning.iconLodLo..iconLodHi ramp). 0 = LOD off
   *  (sparse view / toggle off / unknown viewport — fail-to-default is FULL icons, a display
   *  feature must never hide information on bad data). Rides declutterKey so the size/alpha
   *  accessors' updateTriggers flip exactly when the effective LOD changes. */
  private densityFactor = 0;
  /** Settings toggle: the icon-density LOD master switch (default ON). */
  private iconDensityLod = true;
  /** Settings toggle: blue force immune to the density LOD (default ON). When OFF, friend
   *  degrades at rank 1 (still the gentlest non-immune tier). */
  private blueForceAlwaysProminent = true;
  /** Single-flight guard for the lazy milsymbol chunk kick from rebuildDeck (coalition mode). */
  private milsymbolKickPending = false;
  /** GREEN-DECLUTTER: per-entity halo OPACITY MULTIPLIER [floor..1], recomputed each rebuild
   *  from the live screen-pixel overlap of the soft halos (see {@link computeHaloDetune}). A
   *  halo buried under its neighbours dims so a dense cluster reads as crisp icons, not a green
   *  wash. Presentation-only (alpha only); absent id ⇒ 1 (no detune). */
  private haloDetune = new Map<string, number>();
  /** Map zoom listener (declutter refresh) — removed on dispose(). */
  private readonly onMapZoom = (): void => this.rebuildDeck();

  constructor(opts: DeckBackendOptions) {
    this.map = opts.map;
    this.affiliationColors = opts.affiliationColors;
    this.pointRadiusMeters = opts.pointRadiusMeters ?? 12_000;
    this.onPick = opts.onPick;
    this.markerMode = opts.markerMode ?? DEFAULT_MARKER_MODE;
    this.renderSettings = resolveRenderSettings(opts.renderSettings);
    this.readIconLodToggles(opts.renderSettings);
    this.colorsKey = this.computeColorsKey();
    // interleaved:true puts deck layers in the same WebGL2 context as MapLibre.
    // PERF METER: onAfterRender fires once per PAINTED deck frame — feed glides AND user pan/zoom
    // (interleaved mode ties deck's render to MapLibre's loop) — so the meter measures the true
    // render cadence, including the drag/zoom interaction the pump loop alone would miss. setProps()
    // in rebuildDeck merges (only touches `layers`), so this callback survives every rebuild.
    this.overlay = new MapboxOverlay({
      interleaved: true,
      layers: [],
      onAfterRender: this.onDeckAfterRender,
    });
    this.map.addControl(this.overlay as unknown as IControl);
    // Declutter is zoom-driven (marker scale / label + drop-line gating recompute from zoom).
    // `zoomend` fires once when a zoom gesture settles (low-frequency) — recompute the gated
    // layer DATA then so it stays in step with the camera without thrashing on every rAF.
    this.map.on?.("zoomend", this.onMapZoom);
    // PERF METER: stand up the always-on render-cadence overlay (no-op in headless / ?perfhud=0).
    this.initPerfHud();
  }

  /** Re-push the render settings (fades / ellipse / vector scale / drop-lines) after a Bundle B
   *  change. Resolves with fallback constants (never blocked on a missing key) and rebuilds so the
   *  change is visible immediately. PRESENTATION ONLY — never changes WHAT renders (I13). */
  setRenderSettings(input?: RenderSettingsInput): void {
    this.renderSettings = resolveRenderSettings(input);
    this.readIconLodToggles(input);
    this.rebuildDeck();
  }

  /** Fold the OPTIONAL icon-density-LOD toggles riding the settings blob into backend flags.
   *  The host hands the FULL local-settings object here (a structural superset of
   *  RenderSettingsInput — cop.ts re-pushes getSettings() on every store change), so the two
   *  booleans arrive on the same live seam the fade sliders use. Absent / malformed ⇒ the
   *  shipped defaults (both ON) — resolveRenderSettings-style fail-to-default. */
  private readIconLodToggles(input?: RenderSettingsInput): void {
    const t = input as
      | { iconDensityLod?: unknown; blueForceAlwaysProminent?: unknown }
      | undefined;
    this.iconDensityLod = t?.iconDensityLod !== false; // only an explicit false disables
    this.blueForceAlwaysProminent = t?.blueForceAlwaysProminent !== false;
  }

  /** Current resolved render settings (for the host / tests). */
  getRenderSettings(): RenderSettings {
    return this.renderSettings;
  }

  /** The active MapLibre zoom, guarded for headless/SSR construction (getZoom may be absent on a
   *  stub map). Falls back to the declutter full-detail zoom so nothing is mis-gated when unknown. */
  private currentZoom(): number {
    const z = this.map.getZoom?.();
    return typeof z === "number" && Number.isFinite(z) ? z : 9;
  }

  /** Switch the affiliation carrier at runtime (milsym frame-shape ⇄ legacy dot).
   *  No-op if unchanged. Rebuilds deck so the swap is visible immediately. */
  setMarkerMode(mode: MarkerMode): void {
    if (mode === this.markerMode) return;
    this.markerMode = mode;
    this.rebuildDeck();
  }

  /** Current affiliation carrier. */
  getMarkerMode(): MarkerMode {
    return this.markerMode;
  }

  /** Re-point the affiliation palette after a runtime theme swap. Both render modes
   *  re-resolve from this single source: the dot fill recolours and (because the
   *  colorsKey changes) the milsym sprites re-key and re-rasterise in the new hues
   *  while their FRAME SHAPE — the affiliation carrier — stays constant. */
  setAffiliationColors(colors: AffiliationColors): void {
    this.affiliationColors = colors;
    this.colorsKey = this.computeColorsKey();
    this.rebuildDeck();
  }

  /** A stable string fingerprint of the resolved affiliation palette. */
  private computeColorsKey(): string {
    const c = this.affiliationColors;
    return `${c.friend}|${c.hostile}|${c.neutral}|${c.unknown}|${c.pending}`;
  }

  /** IVM-3: a stable fingerprint of everything SYMBOLOGY — palette + label plate + fade +
   *  ellipse + drop-line opacities + vector scale. Feeds the {@link symbologyKey} updateTrigger
   *  so a marker's colour / opacity accessors re-resolve on a theme / settings swap but NOT on a
   *  mere interaction (which would needlessly re-upload the colour buffers and, if it touched
   *  getPosition, disturb the glide). The LABEL PLATE rides here (not colorsKey) on purpose:
   *  dark↔light themes share the SAME five affiliation hexes, so only the plate distinguishes
   *  them — putting it in colorsKey would force a pointless milsym sprite re-rasterisation on
   *  that swap. Cheap string join, recomputed once per rebuild. */
  private computeSymbologyKey(): string {
    const s = this.renderSettings;
    const dl = s.advanced.dropLine;
    const el = s.advanced.ellipse;
    return [
      this.colorsKey,
      this.affiliationColors.labelPlate ?? "",
      tuning.labelPlateAlpha,
      s.fade.neutral,
      s.fade.unknown,
      dl.lineOpacity,
      dl.groundDotOpacity,
      el.airSigma,
      el.unknownAirSigma,
      el.shipSigma,
      s.advanced.vectorScale,
    ].join("|");
  }

  /** The PROTECTIVE LABEL PLATE colour behind every callsign: the active theme's plate hex
   *  (labelPlate, carried on the affiliation-palette seam so a runtime profile switch re-points
   *  it in place — see AffiliationColors) at tuning.labelPlateAlpha. Returns null when the
   *  plate is disabled (labelPlateAlpha ≤ 0) — the caller restores the full 2px SDF outline so
   *  legibility never regresses with the plate off. Fail-to-default: a theme without the token
   *  gets the near-black dark-family plate (right over the dark-matter basemap). */
  private plateRgba(): RGBA | null {
    const alpha = tuning.labelPlateAlpha;
    if (!(alpha > 0)) return null;
    const [r, g, b] = hexToRgba(this.affiliationColors.labelPlate ?? DEFAULT_LABEL_PLATE);
    return [r, g, b, Math.round(255 * Math.min(0.9, alpha))];
  }

  /** The affiliation palette in symbology.ts's SymbolColors shape. Both the host
   *  AffiliationColors and SymbolColors are keyed by the canonical P1 affiliations
   *  (friend/hostile/neutral/unknown/pending), so this is a direct structural reuse —
   *  the SAME single-sourced palette feeds the dot fill and the milsym frame. */
  private symbolColors(): SymbolColors {
    const c = this.affiliationColors;
    return {
      friend: c.friend,
      hostile: c.hostile,
      neutral: c.neutral,
      unknown: c.unknown,
      pending: c.pending,
    };
  }

  setView(view: ViewState): void {
    // The engine owns logical view; push it to MapLibre (which owns the camera).
    this.map.jumpTo({
      center: [view.longitude, view.latitude],
      zoom: view.zoom,
      ...(view.pitch !== undefined ? { pitch: view.pitch } : {}),
      ...(view.bearing !== undefined ? { bearing: view.bearing } : {}),
    });
  }

  createLayer(spec: LayerSpec): void {
    this.specs.set(spec.id, spec);
  }

  patchLayer(spec: LayerSpec): void {
    // We rebuild deck layer objects each writeFrame from current data, so a
    // spec patch only needs to refresh the stored spec. deck.gl itself does the
    // updateTriggers diffing when we hand it new layer instances with same id.
    this.specs.set(spec.id, spec);
  }

  destroyLayer(id: string): void {
    this.specs.delete(id);
    this.frameData.delete(id);
    this.dataCache.delete(id); // IVM-3: drop the stable-data cache entry for the removed spec
    this.rebuildDeck();
  }

  writeFrame(layerData: ReadonlyMap<string, unknown[]>): void {
    const t0 =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    this.frameData = new Map();
    this.byId.clear();
    const live = new Set<string>();
    // ONE wall-clock read stamps every trail point this frame (buildTrails ages segments against
    // the same Date.now() timeline in rebuildDeck — never mix in perfNow, whose origin differs).
    const wallNowMs = Date.now();
    // Trail knobs resolved once per frame (live-read from tuning, seconds → ms at the seam).
    const trailOpts = {
      maxGapMs: tuning.trailMaxGapS * 1000,
      maxAgeMs: tuning.trailMaxAgeS * 1000,
      teleportNm: MAX_RENDER_SEG_NM,
      maxLen: tuning.trailLen,
    };
    for (const [layerId, data] of layerData) {
      const envs = data as CanonicalEnvelope[];
      this.frameData.set(layerId, envs);
      for (const env of envs) {
        this.byId.set(env.id, env);
        live.add(env.id);
        const p = point3Of(env);
        if (p) {
          // TRAIL APPEND POLICY (trailPolicy.ts, pure): appends only on a real move (no fat dot),
          // BREAKS the history on a spatial teleport (loop-wrap) OR a TIME gap (hidden/throttled
          // tab: rAF pauses this writer while the feed keeps upserting — the refocus jump must
          // never draw as a segment), then prunes by age (contrail dissipation) + caps length.
          const hist = this.trails.get(env.id) ?? [];
          appendTrailPoint(hist, p, wallNowMs, trailOpts);
          this.trails.set(env.id, hist);
          // GPU transition (C): the new position becomes the deck getPosition target; deck glides
          // the dot there on the GPU over transitionMs (no per-track CPU tween — see rebuildDeck).
        }
      }
    }
    // Drop trails for entities no longer present (the GPU transition self-cleans on data diff).
    for (const id of this.trails.keys()) if (!live.has(id)) this.trails.delete(id);
    // IVM-3: a real data write happened — bump the DATA epoch so the three stable-data layers
    // re-resolve their position + data-derived accessors. This is the ONLY place dataVersion moves;
    // interaction / settings / zoom rebuilds leave it fixed, so they never re-run getPosition and
    // never disturb the in-flight GPU glide. (The fresh raw arrays here also naturally invalidate
    // the dataCache by identity — see filteredData.)
    this.dataVersion++;
    // Measure the inter-update interval → the GPU transition duration (clamped), and open the glide
    // window so the pump advances the deck position transition until it settles. A position-only
    // republish at the feed cadence drives this; the GPU does the per-frame interpolation.
    const now = perfNow();
    const interval = this.lastWriteMs > 0 ? now - this.lastWriteMs : TRANSITION_DEFAULT_MS;
    this.lastWriteMs = now;
    this.transitionMs = Math.min(TRANSITION_MAX_MS, Math.max(TRANSITION_MIN_MS, interval));
    this.transitionDeadline = now + this.transitionMs;
    this.rebuildDeck();
    // RE-GATE SNAP consumed: this data frame rendered with transitions disabled (if flagged);
    // the following frames glide normally again.
    this.snapOnNextDataFrame = false;
    this.schedulePump(); // advance the GPU glide; parks itself once the window closes
    const t1 =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    this.lastFrameMsValue = t1 - t0;
  }

  pick(screen: { x: number; y: number }): { id: string } | null {
    const info = this.overlay.pickObject({ x: screen.x, y: screen.y, radius: 6 });
    const obj = info?.object as CanonicalEnvelope | undefined;
    if (obj && typeof obj.id === "string") {
      this.selectedId = obj.id;
      this.onPick?.(obj);
      this.rebuildDeck();
      return { id: obj.id };
    }
    this.selectedId = null;
    this.onPick?.(null);
    this.rebuildDeck();
    return null;
  }

  /** Set the hovered track (the host derives the id from a NON-mutating pick under
   *  the cursor — see pickId). The hovered track's uncertainty smear blooms (same
   *  reveal-on-focus path as selection). No-op if unchanged; rebuilds so the bloom
   *  appears/clears immediately. Pass null on mouse-out to clear. */
  setHovered(id: string | null): void {
    if (id === this.hoveredId) return;
    this.hoveredId = id;
    this.rebuildDeck();
  }

  /** Pick the entity id under a screen point WITHOUT mutating selection or firing
   *  onPick — the read-only sibling of pick(), used for hover. Returns null when the
   *  cursor is over empty map. */
  pickId(screen: { x: number; y: number }): string | null {
    const info = this.overlay.pickObject({ x: screen.x, y: screen.y, radius: 6 });
    const obj = info?.object as CanonicalEnvelope | undefined;
    return obj && typeof obj.id === "string" ? obj.id : null;
  }

  /** A track is EMPHASIZED (its smear blooms) when it is selected or hovered — the
   *  reveal-on-focus interaction state shared by the marker size-bump and the smear
   *  bloom. */
  private isEmphasized(e: CanonicalEnvelope): boolean {
    return e.id === this.selectedId || e.id === this.hoveredId;
  }

  /** SPARSE VIEW: at most tuning.sparseLabelShowAll tracks in the viewport — recomputed each
   *  rebuild (rebuildDeck). Relaxes the label gates: room exists, so everything reads. */
  private sparseView = false;

  /** RE-GATE SNAP (the down-filter "fly-in" fix). deck.gl position transitions are ARRAY-INDEX
   *  aligned (GPU buffer[i]_old → buffer[i]_new): when a visibility RE-GATE (clearance
   *  down-filter / layer gate) REMOVES entities, every survivor shifts to a lower index and
   *  deck glides it FROM the removed entity's old position at that index — icons "flying in
   *  from all directions". The `enter` callback can't help (it fires only for indices past the
   *  old length, and a shifted survivor has a stale prior value at its new index). So the host
   *  flags the membership change and the NEXT data frame renders its position updates with
   *  transitions DISABLED — everything SNAPS into correct place — then the motion glide resumes
   *  on the following frame. Set via notifyMembershipChange (cop.ts delta consumer, removes>0);
   *  cleared at the end of writeFrame. NOT triggered by position-only motion or by age-out
   *  re-inserts (those ride the normal glide). */
  private snapOnNextDataFrame = false;

  /** Host hint: the visible-set membership just shrank (a re-gate removed entities) — snap the
   *  next data frame's positions instead of tweening them. See snapOnNextDataFrame. */
  notifyMembershipChange(): void {
    this.snapOnNextDataFrame = true;
  }

  lastFrameMs(): number {
    return this.lastFrameMsValue;
  }

  allocatedBytes(): number {
    // Coarse estimate: ~one ScatterplotLayer vertex record per entity.
    return this.byId.size * 64;
  }

  dispose(): void {
    try {
      if (this.pumpHandle != null && typeof cancelAnimationFrame !== "undefined") {
        cancelAnimationFrame(this.pumpHandle);
      }
      this.pumpHandle = null;
      // PERF METER: stop the HUD timer and detach the overlay element.
      if (this.perfHudTimer != null) {
        clearInterval(this.perfHudTimer);
        this.perfHudTimer = null;
      }
      this.perfHudEl?.remove();
      this.perfHudEl = null;
      this.map.off?.("zoomend", this.onMapZoom);
      this.map.removeControl(this.overlay as unknown as IControl);
    } catch {
      // best-effort teardown
    }
    this.specs.clear();
    this.frameData.clear();
    this.byId.clear();
    this.dataCache.clear(); // IVM-3
  }

  // ── LIVE INTERPOLATION (C) — GPU transitions own the glide ──────────────────────────────────
  /** The RENDER position of a track = its RAW geometry. deck.gl interpolates between successive raw
   *  positions on the GPU (transitions:{getPosition}), so there is NO per-frame CPU tween: every
   *  layer reads this one chokepoint and the GPU glides the dot, halo, smear, leader + drop-line
   *  together. Kept as a method so all callers are unchanged and a future per-track render offset
   *  has one home. (The deck transition interpolates linearly between the previous and current
   *  getPosition value, keyed by data index — a position jump on a data update glides smoothly.) */
  private renderPoint3(env: CanonicalEnvelope): [number, number, number] | null {
    return point3Of(env);
  }

  /** Pump map repaints while a GPU transition is in flight so deck advances the interpolation
   *  between data rebuilds. A still picture (past the glide deadline) PARKS the pump — zero cost at
   *  rest. The pump does NOT rebuild layers and does NOT re-run the screen-space passes or the
   *  accessors; it only asks the map to repaint, and deck advances the position attribute transition
   *  on the GPU. This is the cheap headroom loop that replaces the old 30fps CPU rebuild. */
  private schedulePump(): void {
    if (this.pumpHandle != null || typeof requestAnimationFrame === "undefined") return;
    // Keep pumping while a GPU glide is in flight OR a hypersonic blip is pulsing. The blip must
    // re-sample its pulse envelope EVERY frame (refreshBlips) or it steps at the ~1 Hz data cadence
    // (choppy) — the pump alone only advances POSITION on the GPU, never the blip radius. No glide
    // AND no active blip → stay parked (zero cost at rest).
    if (perfNow() >= this.transitionDeadline && this.blipInputs.length === 0) return;
    this.pumpHandle = requestAnimationFrame(this.pumpTick);
  }

  private readonly pumpTick = (): void => {
    this.pumpHandle = null;
    if (this.blipInputs.length > 0) {
      // A hypersonic pulse is live: re-sample its envelope against the wall clock and re-push ONLY
      // the ::blip layers. The setProps triggers deck's redraw, which ALSO advances any in-flight
      // position glide — so this subsumes triggerRepaint while a blip is active.
      this.refreshBlips();
    } else {
      // Glide-only: a plain repaint re-renders deck's interleaved layers, interpolating the position
      // attribute buffer toward its target WITHOUT re-running accessors. (The PERF METER measures
      // deck's onAfterRender, which fires for this repaint AND for user pan/zoom frames.)
      this.map.triggerRepaint?.();
    }
    this.schedulePump(); // re-arm while a glide window is open OR a blip pulses; self-parks otherwise
  };

  /** CHOPPY-BLIP FIX: re-sample every active hypersonic pulse against the live clock and re-push
   *  ONLY the ::blip layers into the overlay, leaving every other layer INSTANCE untouched so deck's
   *  prop-diff skips them (no marker/halo/label re-tessellation, and — crucially — the marker
   *  layers' position transition is NOT seen as changed, so the in-flight GPU glide is uninterrupted).
   *  Runs once per pump frame while blipInputs is non-empty, so the ring breathes at frame rate
   *  instead of stepping at the ~1 Hz data cadence. Handing deck a FRESH ::blip data array each
   *  frame re-runs its radius accessor (a new data ref regenerates attributes); the baked pulse
   *  radius therefore advances every frame. PRESENTATION ONLY (I13): the pulse is a cosmetic sensor
   *  blip — WHAT renders (the tracks, their positions, the PDP-visible set) is untouched. */
  private refreshBlips(): void {
    if (this.blipInputs.length === 0 || this.currentLayers.length === 0) return;
    const nowMs = Date.now();
    const fresh = new Map<string, Layer>();
    for (const { id, data } of this.blipInputs) {
      fresh.set(`${id}::blip`, this.buildBlips(id, data, nowMs));
    }
    // Swap the freshly-sampled ::blip layers in by id; keep every other layer instance as-is.
    const layers = this.currentLayers.map((l) => fresh.get(l.id) ?? l);
    this.currentLayers = layers;
    this.overlay.setProps({ layers });
  }

  // ── PERF METER ──────────────────────────────────────────────────────────────────────────────
  /** deck.gl onAfterRender hook — fires once per PAINTED deck frame: feed glides AND user pan/zoom
   *  (interleaved mode ties deck's render to MapLibre's render loop). This is the true per-frame
   *  signal the pump loop missed — map-interaction frames never enter pumpTick — so the meter now
   *  reflects the drag/zoom cadence operators actually complain about. Zero-alloc: folds the interval
   *  in. deck hands a {gl} param; unused here (a nullary handler is assignable to the prop). */
  private readonly onDeckAfterRender = (): void => {
    this.sampleFrame();
  };

  /** Fold the wall-clock interval since the previous painted frame into the FPS / frame-time EMAs.
   *  Called once per deck onAfterRender (an actual paint). Numeric-only — NO allocation on this hot
   *  path. Guards div-by-zero and the idle/restart discontinuity: a gap longer than MAX_FRAME_GAP_MS
   *  is deck resuming after a static-view pause (no interaction / no feed), not a slow frame, so it is
   *  dropped rather than tanking the average. While deck is not repainting this is simply not called,
   *  and after FRAME_IDLE_MS the reading goes "idle" — the honest "nothing is rendering" signal. */
  private sampleFrame(): void {
    const now = perfNow();
    const prev = this.lastFrameClock;
    this.lastFrameClock = now;
    if (prev === 0) return;                          // first frame — no interval to measure yet
    const dt = now - prev;
    // Skip sub-frame bursts (onAfterRender can fire >1×/painted frame → tiny dt) AND park/restart
    // gaps — neither is a real frame interval. Only smooth the FRAME-TIME (ms); fps is derived from
    // it at read time (1000/ms) so the two can never diverge (averaging 1000/dt directly spikes to
    // thousands on any tiny dt — the bug this replaces).
    if (dt < MIN_FRAME_MS || dt > MAX_FRAME_GAP_MS) return;
    this.frameIntervalEmaMs =
      this.frameIntervalEmaMs === 0 ? dt : this.frameIntervalEmaMs + FPS_EMA_ALPHA * (dt - this.frameIntervalEmaMs);
  }

  /** The current smoothed render cadence — for a host readout (e.g. folding into CopStatus) or a test.
   *  `idle` is true when no frame has painted within FRAME_IDLE_MS (deck is not repainting — a static
   *  view with no interaction or feed); fps / frameMs are then zeroed so a stale value never reads as
   *  live throughput. Presentation-only. */
  renderMeter(): { fps: number; frameMs: number; idle: boolean } {
    const idle = this.lastFrameClock === 0 || perfNow() - this.lastFrameClock > FRAME_IDLE_MS;
    // fps DERIVED from the smoothed frame-time (never a separate EMA) → always the honest reciprocal.
    const frameMs = this.frameIntervalEmaMs;
    return idle || frameMs <= 0
      ? { fps: 0, frameMs: 0, idle: true }
      : { fps: 1000 / frameMs, frameMs, idle: false };
  }

  /** Stand up the always-on corner overlay (bottom-left, monospace, low-opacity) and drive its text
   *  off a low-frequency timer so the DOM is touched ~4 Hz, NEVER per rendered frame (no React, no
   *  re-render storm). Suppressed with ?perfhud=0; skipped entirely in headless / SSR / test (no
   *  document). Measurement (sampleFrame) still runs regardless, so renderMeter() stays live. */
  private initPerfHud(): void {
    if (typeof document === "undefined") return;
    if (typeof window !== "undefined") {
      try {
        if (new URLSearchParams(window.location.search).get("perfhud") === "0") return;
      } catch {
        /* window.location unavailable → keep the HUD on */
      }
    }
    const el = document.createElement("div");
    el.setAttribute("data-cop-perf-hud", "");
    el.style.cssText = [
      "position:fixed", "left:8px", "bottom:8px", "z-index:2147483000",
      "font:11px/1.35 ui-monospace,SFMono-Regular,Menlo,monospace",
      "color:#7CFC9A", "background:rgba(0,0,0,0.45)", "padding:2px 7px",
      "border-radius:4px", "pointer-events:none", "user-select:none",
      "opacity:0.6", "letter-spacing:0.02em", "white-space:nowrap",
    ].join(";");
    el.textContent = "-- fps";
    (this.map.getContainer?.() ?? document.body).appendChild(el);
    this.perfHudEl = el;
    this.perfHudTimer = setInterval(this.paintPerfHud, PERF_HUD_REFRESH_MS);
  }

  /** Repaint the corner overlay from the current smoothed reading. Runs off the HUD timer (~4 Hz), so
   *  it keeps updating — and can flip to "idle" — even while the render pump is parked. The ONLY
   *  allocation in the meter lives here (one small template string, 4×/s), never on the hot path. */
  private readonly paintPerfHud = (): void => {
    const el = this.perfHudEl;
    if (!el) return;
    const m = this.renderMeter();
    el.textContent = m.idle
      ? "idle · -- fps"
      : `${m.fps.toFixed(0)} fps · ${m.frameMs.toFixed(1)} ms`;
  };

  /** IVM-3: the renderable (finite-geometry) subset for a spec, with a STABLE array identity across
   *  rebuilds that did not change the underlying data. The engine hands a FRESH raw array every
   *  writeFrame (engine.tick maps a new array per frame), so comparing raw-array identity is an
   *  exact "did the data change" test — no version counter needed. On an interaction / settings /
   *  zoom rebuild the raw array is unchanged ⇒ the cached filtered ref is returned, and deck.gl —
   *  seeing the same `data` prop — skips the full attribute regen and consults updateTriggers only
   *  (the C2-RENDER-ENGINE "updateTriggers discipline" the blanket-frameN rebuild used to defeat).
   *  On a real data write the raw array is new ⇒ a fresh filtered array is built (deck re-diffs the
   *  changed positions, exactly as before). The filter predicate (finite geometry) depends only on
   *  geometry, so it is safe to reuse across a palette / fade / selection change. */
  private filteredData(id: string): CanonicalEnvelope[] {
    return this.cacheEntry(id).filtered;
  }

  /** The full cached subset record for a spec (see {@link dataCache} / {@link filteredData}).
   *  The marker/alert split is derived HERE, alongside the finite-geometry filter, so its array
   *  identities are exactly as stable as `filtered` — deck's prop-diff + updateTriggers
   *  discipline holds for the split layers too. Branches on e.kind === "alert" ONLY (never
   *  affiliation — NWS/USGS publish neutral alerts, the gateway births hostile ones). */
  private cacheEntry(id: string): {
    raw: CanonicalEnvelope[];
    filtered: CanonicalEnvelope[];
    markers: CanonicalEnvelope[];
    alerts: CanonicalEnvelope[];
    dotRounds: CanonicalEnvelope[];
    dotDiamonds: CanonicalEnvelope[];
  } {
    const raw = this.frameData.get(id) ?? EMPTY_FRAME;
    const cached = this.dataCache.get(id);
    if (cached && cached.raw === raw) return cached;
    const filtered = raw.filter((e) => pointOf(e) !== null);
    const alerts = filtered.filter((e) => e.kind === "alert");
    // No alerts (the common case) ⇒ reuse the SAME filtered ref for the marker subset, so the
    // marker layers' data identity is unchanged from the pre-split behaviour.
    const markers = alerts.length === 0 ? filtered : filtered.filter((e) => e.kind !== "alert");
    // DOT-MODE hostile/other split, derived HERE (not per-rebuild) for the same array-identity
    // stability as the alert split. Computed regardless of the active mode so a marker-mode
    // toggle never invalidates the cache; two O(N) passes at DATA cadence (~1 Hz), same class
    // as the filters above. No hostiles ⇒ same-ref reuse (identical to the alert-split idiom).
    const hostiles = markers.filter((e) => e.properties.affiliation === "hostile");
    // Empty ⇒ the shared EMPTY_FRAME ref (a stable identity even across data writes).
    const dotDiamonds = hostiles.length === 0 ? EMPTY_FRAME : hostiles;
    const dotRounds =
      hostiles.length === 0
        ? markers
        : markers.filter((e) => e.properties.affiliation !== "hostile");
    const entry = { raw, filtered, markers, alerts, dotRounds, dotDiamonds };
    this.dataCache.set(id, entry);
    return entry;
  }

  /** Rebuild deck layers from the current frame. Each track spec renders as a
   *  COMPOSITE — motion trail (bottom), velocity leader, soft halo, then the
   *  marker (top) — so the picture reads alive, not as static dots. */
  private rebuildDeck(): void {
    this.frameN++;
    // CHOPPY-BLIP FIX: recaptured below for every spec carrying a live hypersonic pulse, so the
    // pump can re-sample the pulse at frame rate (refreshBlips). Reset each rebuild.
    this.blipInputs = [];
    const layers: Layer[] = [];
    // The label (::callsign) layers are collected separately and appended AFTER the spec loop —
    // see the "LABELS PAINT LAST" note at the bottom of this method.
    const labelLayers: Layer[] = [];
    // One wall-clock read per rebuild drives every track's staleness ramp; all
    // clouds in a frame age against the same `now` (cheap + consistent).
    const nowMs = Date.now();
    // ZOOM-OUT DECLUTTER: recompute the marker-scale / label-gate / aggregate plan from the live
    // zoom + total entity count ONCE per rebuild. markerScale shrinks markers as you zoom out;
    // labelsVisible gates the callsign overlay; the quantized declutterKey is the updateTrigger
    // (flips only across buckets, so a continuous zoom does not thrash the size buffers).
    let total = 0;
    for (const [, d] of this.frameData) total += d.length;
    // DENSE MODE: at extreme dot counts, drop the per-rebuild decoration overlays (see the layer
    // loop) so the marker picture stays renderable — the rebuild bottleneck is the O(total)
    // decoration construction, not the GPU drawing the dots.
    const dense = total > tuning.decorationMaxEntities;
    this.declutter = declutterPlan({ zoom: this.currentZoom(), entityCount: total });
    // SPARSE-VIEW DETECTION: every label gate exists to stop a WALL — but a sparse viewport has
    // no wall. Computed EVERY rebuild (cheap lng/lat bbox count with an early exit — no
    // projection) because it relaxes TWO things: (1) the zoom-based labelsVisible gate below, and
    // (2) the affiliation gate + truncation cap inside callsignOf — so a lone neutral weather
    // alert filling a zoomed-IN screen gets its headline label too, not just zoomed-out views.
    // Riding the SAME declutterKey keeps the updateTrigger flipping exactly with the effective
    // gate. Host-side by design: the pure declutterPlan stays viewport-ignorant.
    // ONE bounded viewport scan feeds BOTH viewport-density consumers: the sparse-view label
    // relaxation (count ≤ sparseLabelShowAll) and the ICON-DENSITY LOD ramp (count across
    // iconLodLo..iconLodHi). The cap keeps the early exit bounded at the largest threshold + 1;
    // countInViewport still returns the exact count below the cap, so both comparisons are
    // unchanged from the two-scan behaviour. Infinity (headless / unknown viewport) reads as
    // DENSE for the sparse gate (never relax on bad data) and as LOD-OFF below (never fade
    // information on bad data) — each consumer fails to ITS safe side.
    const sparseCap = tuning.sparseLabelShowAll;
    const lodOn = this.iconDensityLod && total > 0;
    const scanCap = Math.max(sparseCap > 0 ? sparseCap : 0, lodOn ? tuning.iconLodHi : 0) + 1;
    // ICON-DENSITY LOD (Intra-Tier-A): densityFactor ramps 0→1 as the in-viewport icon count
    // crosses iconLodLo→iconLodHi, QUANTIZED to 0.05 steps so the declutterKey (and with it the
    // size/alpha updateTriggers) flips only when the effective LOD actually changes. The per-
    // entity multipliers (densitySizeMul / fadeMul) scale by affiliation rank — blue force stays
    // full (see densityRank). PRESENTATION ONLY (I13): a faded icon is still present + pickable.
    // BOTH consumers (sparse-view relaxation + LOD ramp) read ONE bounded viewport scan.
    let df = 0;
    if (dense) {
      // DENSE short-circuit: countInViewport is O(total) when FEWER than scanCap points are
      // actually in view (its early exit only fires in the dense-viewport case), so at the
      // 100k ceiling a zoom into a sparse region would walk all N every rebuild. Above `dense`
      // (total > decorationMaxEntities) decorations are already shed, sparse-view is moot, and
      // the LOD is saturated — so skip the walk: never sparse, LOD fully on.
      this.sparseView = false;
      df = lodOn ? 1 : 0;
    } else {
      // Infinity (headless / unknown viewport) reads as DENSE for the sparse gate (never relax
      // on bad data) and as LOD-OFF below (never fade information on bad data) — each consumer
      // fails to ITS safe side.
      const visN =
        total > 0 && scanCap > 1 ? this.countInViewport(scanCap) : Number.POSITIVE_INFINITY;
      this.sparseView = total > 0 && sparseCap > 0 && visN <= sparseCap;
      if (this.sparseView && !this.declutter.labelsVisible) {
        this.declutter = { ...this.declutter, labelsVisible: true };
      }
      if (lodOn && Number.isFinite(visN)) {
        const lo = tuning.iconLodLo;
        const hi = Math.max(lo + 1, tuning.iconLodHi); // renderTuning enforces Hi>Lo; belt-and-suspenders
        df = Math.min(1, Math.max(0, (visN - lo) / (hi - lo)));
        df = Math.round(df * 20) / 20; // 0.05 quantization
      }
    }
    this.densityFactor = df;
    this.declutterKey = `${this.declutter.markerScale.toFixed(2)}|${this.declutter.labelsVisible ? 1 : 0}|${df.toFixed(2)}|${this.blueForceAlwaysProminent ? 1 : 0}`;
    // IVM-3: refresh the disjoint updateTrigger fingerprints once per rebuild. symbologyKey moves
    // only on a theme / settings swap; interactionKey moves on select / hover / declutter bucket.
    // dataVersion (the third trigger) moves ONLY in writeFrame — so an interaction touches neither
    // it nor the position accessors, leaving the GPU glide undisturbed.
    this.symbologyKey = this.computeSymbologyKey();
    this.interactionKey = `${this.selectedId ?? ""}|${this.hoveredId ?? ""}|${this.declutterKey}`;
    // GREEN-DECLUTTER: recompute the per-marker halo opacity detune from the live screen-pixel
    // overlap ONCE per rebuild (now at DATA/zoom cadence, not 30fps — the rebuild no longer runs
    // per interpolation frame). Pan is invariant (relative pixel offsets are preserved), zoom
    // re-derives on the zoomend rebuild. SCALE CAP: above HALO_DETUNE_MAX_ENTITIES the detune is
    // skipped (empty map ⇒ every multiplier 1, full opacity — fails SAFE, never brighter): it is
    // near-O(N²) in a dense cluster and at that density the markers merge into a field where the
    // per-marker detune is imperceptible anyway.
    if (total <= tuning.haloDetuneMaxEntities) this.computeHaloDetune();
    else this.haloDetune.clear();
    // LABEL-DEDUPE registry: fresh each rebuild pass. Claims accumulate ACROSS the spec loop
    // below, so a string an earlier spec's ::callsign layer draws at a pixel is never re-drawn
    // by a later spec's (the engine fans one entity into every kind-matching spec) — and within
    // one spec, an echoed same-name twin at the same point is never drawn twice.
    this.labelClaims.clear();
    const zoom = this.currentZoom();
    // COALITION LAZY-LOAD: the milsymbol chunk loads on first use (dynamic import). Until it
    // arrives the marker carrier falls back to the milsym frames for THIS rebuild; on arrival
    // one rebuild swaps the ::coalition layer in (mode-distinct ids ⇒ a clean class swap).
    let effectiveMode: MarkerMode = this.markerMode;
    if (effectiveMode === "coalition" && !milsymbolReady()) {
      if (!this.milsymbolKickPending) {
        this.milsymbolKickPending = true;
        void loadMilsymbol().then((ok) => {
          this.milsymbolKickPending = false;
          if (ok && this.markerMode === "coalition") this.rebuildDeck();
        });
      }
      effectiveMode = "milsym"; // render the milsym layer while the chunk loads
    }
    for (const [id] of this.specs) {
      // IVM-3: the renderable subset with a STABLE array identity (see cacheEntry) — reused
      // across interaction / settings / zoom rebuilds so deck honours updateTriggers instead of
      // regenerating every attribute for all N. `data` (everything) feeds the decoration +
      // label layers; `markers`/`alerts` split kind:"alert" out of the unit-marker layers and
      // into the ::alertglyph layer (alerts wear an event glyph, not a unit frame).
      const entry = this.cacheEntry(id);
      const data = entry.filtered;
      // SMEAR FIRST — the Gaussian uncertainty cloud renders BEHIND everything so
      // the crisp affiliation frames sit ON TOP of (never inside) the soft haze.
      layers.push(this.buildSmear(id, data, nowMs));
      // DENSE-MODE GATE: above DECORATION_MAX_ENTITIES the decoration passes (trails, future paths,
      // velocity leaders, halo, drop-lines, blips, crowd-pips, callsigns) — each O(total) per rebuild
      // — dominate the frame AND read as noise at that density, so we skip them and render just the
      // markers. This is what keeps a 100k-dot picture renderable (the markers stay on the GPU). The
      // decorations return below the threshold / on a filter — PRESENTATION ONLY (I13): the same
      // markers (WHAT renders) are unchanged; only the decoration overlays drop.
      if (!dense) {
        // Past (comet-tail history) + future (dead-reckoned projection) both sit
        // BEHIND the heading/halo/stalk/marker stack: the marker always reads on top.
        layers.push(this.buildTrails(id, data, nowMs));
        layers.push(this.buildFuturePath(id, data));
        layers.push(this.buildHeading(id, data));
        layers.push(this.buildHalo(id, data));
        // 2.5D-DEPTH: vertical drop-line(s) from surface to lifted air/UAS glyph (under the marker)
        // — the ground dot + stalk, zoom/altitude-gated (declutter), returned as a small layer set.
        for (const dl of this.buildDropLines(id, data, zoom)) layers.push(dl);
      }
      // Affiliation carrier — THREE-WAY: milsym frame-SHAPE (default, channel-law-correct),
      // the legacy colour-coded dot, or full milsymbol 2525 symbols (coalition; falls back to
      // milsym while its chunk loads — see effectiveMode above). All sit atop the same
      // trail/heading/halo stack. ALWAYS rendered — the markers are the irreducible picture.
      // kind:"alert" envelopes are EXCLUDED here (entry.markers) and rendered exclusively by
      // the ::alertglyph layer below — an alert wears an event glyph, never a unit frame.
      if (effectiveMode === "dot") {
        // MINIMAL mode is TWO marker layers: the classic circles, plus the HOSTILE DIAMOND
        // (rotated-square) icon layer for hostile-affiliation tracks — the 2525 hostile
        // frame shape carried into the low-ink carrier so hostility never depends on hue
        // alone (the MONO master test). Always pushed (EMPTY_FRAME when no hostiles ⇒ no
        // cost) so the layer identity is stable across data writes.
        layers.push(this.buildMarkers(id, entry.dotRounds));
        layers.push(this.buildDotDiamonds(id, entry.dotDiamonds));
      } else {
        layers.push(
          effectiveMode === "coalition"
            ? this.buildCoalitionMarkers(id, entry.markers)
            : this.buildMilsymMarkers(id, entry.markers),
        );
      }
      // ALERT GLYPH layer — ABOVE the marker layer in every mode (and NOT dense-gated: alerts
      // are the picture's most operationally-urgent envelopes and the subset is small).
      layers.push(this.buildAlertGlyphs(id, entry.alerts));
      if (!dense) {
        // HYPERSONIC BLIP — a periodic pulse (cadence ∝ speed) on very-fast tracks, over the marker.
        // Capture the (tiny) hypersonic subset so the pump re-samples the pulse at frame rate
        // (refreshBlips) — the ring must breathe smoothly, not step at the ~1 Hz data cadence.
        const hs = data.filter((e) => (numProp(e, "speed_kn") ?? 0) >= HYPERSONIC_MIN_KN);
        if (hs.length > 0) this.blipInputs.push({ id, data: hs });
        layers.push(this.buildBlips(id, hs, nowMs));
        // Living-Net CROWD costume — the falsifiable inner-pip, ON TOP of the marker
        // so it tags the frame (DESIGN-SPEC §2 CO-HERO part 2). A NON-hue/opacity/
        // dash/shape channel: it cannot collide with any owned channel.
        layers.push(this.buildCrowdPips(id, data));
        // CALLSIGN LABELS — each track's callsign beneath the dot, in affiliation colour (the
        // TEXT is the channel, colour only confirms → survives MONO). Zoom-gated (declutter).
        // BUILT here — inside the spec loop, so the cross-spec label-dedupe claim order stays
        // exactly the spec registration order — but PUSHED after the loop (labelLayers below).
        labelLayers.push(this.buildCallsigns(id, data));
      }
    }
    // LABELS PAINT LAST — ABOVE every marker / halo / alert-glyph / blip across ALL specs.
    // WHY: deck composites in array order, and the per-spec loop used to push each spec's
    // ::callsign layer before the NEXT spec's marker stack — so any later spec's icons painted
    // OVER an earlier spec's callsigns (icon-eats-text). Labels (and their protective plates)
    // are the operator's READING layer: a marker occluding text is strictly worse than text
    // near a marker — the marker still reads by shape/colour under a plate's translucency,
    // but occluded text reads as nothing. So the whole label family composites above the
    // whole marker family, deliberately.
    for (const l of labelLayers) layers.push(l);
    // CHOPPY-BLIP FIX: stash the base array the per-frame blip refresh swaps ::blip layers into,
    // then (re)arm the pump — it self-parks unless a glide is live OR a hypersonic is pulsing.
    this.currentLayers = layers;
    this.overlay.setProps({ layers });
    this.schedulePump();
  }

  private colorFor(env: CanonicalEnvelope): RGBA {
    const aff = env.properties.affiliation;
    const hex = (aff && this.affiliationColors[aff]) || FALLBACK_COLOR;
    // Dim entities whose payload is sealed (clear-shell) slightly less opaque.
    const alpha = env.security.payload_mode === "encrypted" ? 180 : 235;
    return hexToRgba(hex, alpha);
  }

  private isAlert(e: CanonicalEnvelope): boolean {
    return e.kind === "alert" || e.properties.affiliation === "hostile";
  }
  /** FRIENDLY affiliation — friendlies get callsign PRIORITY: their labels are always shown (never
   *  zoom-decluttered away) and never shrunk below legibility. An operator must always be able to
   *  read who their own forces are. */
  private isFriendly(e: CanonicalEnvelope): boolean {
    return e.properties.affiliation === "friend";
  }
  /** TDF-protected = the PDP flagged it, or it carries an encrypted payload /
   *  non-clear leakage profile. Drives the on-map "secure ring" + inspector badge. */
  private isProtected(e: CanonicalEnvelope): boolean {
    return (
      (e.properties as Record<string, unknown>).tdf_protected === true ||
      e.security.payload_mode === "encrypted" ||
      (e.security.leakage_profile != null && e.security.leakage_profile !== "clear")
    );
  }
  private affRgb(e: CanonicalEnvelope): RGBA {
    const aff = e.properties.affiliation;
    return hexToRgba((aff && this.affiliationColors[aff]) || FALLBACK_COLOR, 255);
  }

  /** ICON-DENSITY LOD rank [1..GLOBAL_RANK_MAX] — which tier an entity degrades in when the
   *  viewport is dense: globalRank's ICON variant (v2 §1.1; alert 1, friend 2, hostile 3,
   *  unknown/pending 4, neutral 5 — a neutral NWS alert never thins out with the neutral
   *  haystack). NO emphasis tier — keeping the LOD hover-independent avoids the updateTriggers
   *  staleness hazard (marker colour/size triggers deliberately exclude interaction state).
   *  The blueForceAlwaysProminent exemption lives in the MULTIPLIERS below (an explicit friend
   *  branch — the old rank-0 mathematical immunity no longer exists); with the toggle OFF,
   *  friend scales at its rank 2 (penalty 0.4·df·shrink — slightly firmer than the shipped
   *  0.25, accepted in v2 §1.1; hostile likewise 0.6 — hostile icons DO thin in extreme
   *  density, alerts stay). */
  private densityRank(e: CanonicalEnvelope): number {
    return globalRankIcon(e.kind, e.properties.affiliation);
  }

  /** ICON-DENSITY LOD per-entity SIZE multiplier: 1 − densityFactor·iconLodShrink·
   *  (rank/GLOBAL_RANK_MAX), floored at 1 − iconLodShrink (densityLodMul — the v2 §1.1 divisor
   *  fix; bounds T0-pinned in render-engine: iconLodShrink ≤ 0.8 keeps the rank-5 floor ≥ 0.2
   *  of full size, never zero/negative). blueForceAlwaysProminent ON ⇒ friend is EXEMPT
   *  (explicit branch, multiplier exactly 1). 1 whenever the LOD is off. */
  private densitySizeMul(e: CanonicalEnvelope): number {
    const df = this.densityFactor;
    if (df <= 0) return 1;
    if (this.blueForceAlwaysProminent && this.isFriendly(e)) return 1; // blue force never thins
    return densityLodMul(this.densityRank(e), df, tuning.iconLodShrink);
  }

  /** ICON-DENSITY LOD per-entity ALPHA multiplier: 1 − densityFactor·iconLodFade·
   *  (rank/GLOBAL_RANK_MAX), floored at 1 − iconLodFade (iconLodFade ≤ 0.9 ⇒ floor ≥ 0.1 —
   *  never negative alpha). Folded into {@link fadeMul} so every alpha consumer (dot fill,
   *  milsym/coalition tint, halo, alert glyph) degrades coherently. blueForceAlwaysProminent
   *  ON ⇒ friend is EXEMPT (explicit branch — same doctrine as densitySizeMul). */
  private densityAlphaMul(e: CanonicalEnvelope): number {
    const df = this.densityFactor;
    if (df <= 0) return 1;
    if (this.blueForceAlwaysProminent && this.isFriendly(e)) return 1; // blue force never fades
    return densityLodMul(this.densityRank(e), df, tuning.iconLodFade);
  }

  /** CONFIGURABLE FADE — the per-affiliation opacity multiplier [0..1] that cuts clutter.
   *  NEUTRAL fades to fade.neutral (default 0.20) and UNKNOWN to fade.unknown (default 0.70);
   *  friend / hostile / pending are never affiliation-faded (multiplier 1). Read from Bundle B's
   *  settings with the fallback constants (renderSettingsKeys.ts). The ICON-DENSITY LOD alpha
   *  multiplier ({@link densityAlphaMul}) rides on top: in a dense viewport lower-priority
   *  icons dim further, by affiliation rank. PRESENTATION ONLY — a faded track is still
   *  present + pickable; the fade never changes WHAT the projection renders (I13). */
  private fadeMul(e: CanonicalEnvelope): number {
    const aff = e.properties.affiliation;
    const base =
      aff === "neutral"
        ? this.renderSettings.fade.neutral
        : aff === "unknown"
          ? this.renderSettings.fade.unknown
          : 1;
    return base * this.densityAlphaMul(e);
  }

  /** Apply {@link fadeMul} to an RGBA's alpha channel (RGB untouched). */
  private faded(e: CanonicalEnvelope, [r, g, b, a]: RGBA): RGBA {
    return [r, g, b, Math.round(a * this.fadeMul(e))];
  }

  /** Markers — constant SCREEN-pixel size (zoom-independent), affiliation-filled;
   *  selected gets a white halo (selection = halo, the sole sanctioned ring). The
   *  protected status is NOT drawn on the map (DESIGN-SPEC §2.4 / channel budget);
   *  it lives in the inspector on select. */
  private buildMarkers(id: string, data: CanonicalEnvelope[]): Layer {
    return new ScatterplotLayer<CanonicalEnvelope>({
      // Mode-distinct id: milsym is an IconLayer, dot is a ScatterplotLayer. Sharing
      // one id made deck.gl try to UPDATE one class into the other on toggle and bleed
      // the IconLayer's getSize accessor onto the ScatterplotLayer ("getSize is not a
      // function"). Distinct ids → deck finalizes one + inits the other, clean swap.
      id: `${id}::dot`,
      data,
      pickable: true,
      stroked: true,
      filled: true,
      radiusUnits: "pixels",
      radiusMinPixels: 3,
      radiusMaxPixels: 16,
      lineWidthUnits: "pixels",
      getPosition: (e) => this.renderPoint3(e) ?? [0, 0, 0],
      // DECLUTTER: markers shrink toward a floor as you zoom out (declutter.markerScale).
      // ICON-DENSITY LOD: lower-priority icons shrink further in a dense viewport (rank-scaled;
      // blue force immune — densitySizeMul).
      getRadius: (e) =>
        ((this.isAlert(e) ? tuning.markerPx + 2.5 : tuning.markerPx) + (e.id === this.selectedId ? 2 : 0)) *
        this.declutter.markerScale *
        this.densitySizeMul(e),
      // 2.5D-DEPTH: lighten+desaturate the dot by altitude (2D-legible, MONO-safe, RGB-only).
      // CONFIGURABLE FADE: neutral/unknown fills dim per the fade settings (clutter cut).
      getFillColor: (e) => this.faded(e, altTint(this.colorFor(e), zOf(e))),
      getLineColor: (e) =>
        e.id === this.selectedId ? [255, 255, 255, 255] : [9, 12, 17, 235],
      getLineWidth: (e) => (e.id === this.selectedId ? 2.5 : 1),
      // 2.5D-DEPTH: composite in painter's order at tilt (don't depth-sort against the basemap).
      parameters: FLAT_OVERLAY_DEPTH,
      // GPU GLIDE: deck interpolates getPosition between data updates over the measured feed
      // interval, so the dot slides smoothly without a per-frame CPU rebuild (the headroom path).
      // enter:(v)=>v — a NEW entity enters AT its target position, not deck's default [0,0,0]
      // (null island). Without it, every first-seen track (incl. the whole set on first load)
      // glides in from off West Africa — the "icons finding their chairs" fly-in.
      // RE-GATE SNAP: transitions disable for the one membership-change frame (see
      // snapOnNextDataFrame) — else a survivor glides from the removed entity's old index slot.
      transitions: this.snapOnNextDataFrame
        ? {}
        : { getPosition: { duration: this.transitionMs, enter: (v: number[]) => v } },
      updateTriggers: {
        // IVM-3 disjoint triggers. fill = affiliation + altitude (data) × palette + fade
        // (symbology) × the density-LOD alpha (declutterKey carries the quantized
        // densityFactor); it re-resolves on a theme / fade / LOD-bucket change, NOT on a hover.
        getFillColor: [this.dataVersion, this.symbologyKey, this.declutterKey],
        // radius = isAlert (data) × selection + declutter + density-LOD (interaction).
        getRadius: [this.dataVersion, this.interactionKey],
        // the selection ring (white stroke) = selection state only (interaction).
        getLineColor: this.interactionKey,
        getLineWidth: this.interactionKey,
        // position = DATA only — never re-run on hover / select / zoom, so the in-flight GPU
        // transition glides on uninterrupted (the interaction-jerk fix).
        getPosition: this.dataVersion,
      },
    });
  }

  /** DOT-MODE HOSTILE DIAMOND — in the Minimal ("dot") carrier every marker used to be a
   *  circle, so hostility read ONLY through hue: strip the hue (the MONO master test) and a
   *  hostile was indistinguishable from a friend. Hostile-affiliation markers now render as a
   *  ROTATED-SQUARE (diamond) dot — the MIL-STD-2525 hostile frame shape carried into the
   *  low-ink carrier — via a MASK IconLayer tinted with the exact same per-entity fill the
   *  circle dots use (affiliation hue + altitude tint + encrypted-payload alpha + fade +
   *  density-LOD), so every colour/alpha channel is preserved; only the SHAPE changes.
   *  Selection reads as the size bump (mirrors the milsym carrier; the circle's white stroke
   *  has no mask-sprite equivalent). kind:"alert" envelopes never reach here (they wear the
   *  ::alertglyph). Sizing mirrors buildMarkers' hostile radius (markerPx+2.5, the isAlert
   *  bump) ×2 (radius→extent) ×DOT_DIAMOND_MUL (visual-weight parity — see the constant). */
  private buildDotDiamonds(id: string, data: CanonicalEnvelope[]): Layer {
    return new IconLayer<CanonicalEnvelope>({
      // Mode-distinct id (see buildMarkers): never cross-updated into the ::dot Scatterplot.
      id: `${id}::dotdiamond`,
      data,
      pickable: true,
      getPosition: (e) => this.renderPoint3(e) ?? [0, 0, 0],
      // ONE palette-independent mask sprite for every hostile diamond; `mask: true` makes deck
      // read only the sprite's alpha and take the fill from getColor (the full dot pipeline).
      getIcon: () => {
        const ic = dotDiamondIcon();
        return {
          url: ic.url,
          width: ic.width,
          height: ic.height,
          anchorX: ic.anchorX,
          anchorY: ic.anchorY,
          id: "dotshape|diamond",
          mask: true,
        };
      },
      sizeUnits: "pixels",
      // Tip-to-tip extent = the equivalent hostile circle's diameter × DOT_DIAMOND_MUL.
      // Every hostile dot carries the isAlert +2.5 bump (isAlert ⊇ hostile), kept explicit for
      // formula parity with buildMarkers/labelBelowPx; selection bumps like the circle.
      getSize: (e) =>
        2 *
        DOT_DIAMOND_MUL *
        (((this.isAlert(e) ? tuning.markerPx + 2.5 : tuning.markerPx) +
          (e.id === this.selectedId ? 2 : 0)) *
          this.declutter.markerScale *
          this.densitySizeMul(e)),
      // Mirror the circle's radiusMinPixels 3 / radiusMaxPixels 16 clamp, ×2 (diameter) ×MUL.
      sizeMinPixels: Math.round(2 * DOT_DIAMOND_MUL * 3),
      sizeMaxPixels: Math.round(2 * DOT_DIAMOND_MUL * 16),
      // The EXACT circle-dot fill: affiliation hue + altitude tint + encrypted alpha + fade.
      getColor: (e) => this.faded(e, altTint(this.colorFor(e), zOf(e))),
      // 2.5D-DEPTH: composite in painter's order at tilt (don't depth-sort against the basemap).
      parameters: FLAT_OVERLAY_DEPTH,
      // GPU GLIDE + RE-GATE SNAP: identical discipline to the other marker layers.
      transitions: this.snapOnNextDataFrame
        ? {}
        : { getPosition: { duration: this.transitionMs, enter: (v: number[]) => v } },
      updateTriggers: {
        // Mirrors ::dot: fill = data × symbology × density-LOD bucket; size = data × interaction;
        // position = DATA only (the interaction-jerk fix). The icon is a single constant sprite.
        getColor: [this.dataVersion, this.symbologyKey, this.declutterKey],
        getSize: [this.dataVersion, this.interactionKey],
        getPosition: this.dataVersion,
      },
    });
  }

  /** The domain (air/maritime/ground/uas/space) used to pick the 2525 dimension
   *  modifier + central glyph. It is a free-form property; symbology.ts normalises
   *  unknown values to "ground", so any of these being absent is safe. */
  private domainOf(e: CanonicalEnvelope): string {
    const p = e.properties as Record<string, unknown>;
    const d = p.domain ?? p.type ?? "";
    return typeof d === "string" ? d : "";
  }

  /** A stable per-sprite key. deck.gl dedupes the IconLayer texture upload by the
   *  icon `id`, and symbology.ts caches the rasterised sprite by the SAME tuple, so
   *  each distinct (affiliation, domain, palette) symbol is generated exactly once and
   *  reused every frame — never regenerated in the 16.7ms budget. Alert state widens
   *  the rendered size (getSize), not the sprite bitmap, so it stays out of the key. */
  private symbolKey(e: CanonicalEnvelope): string {
    // NORMALIZE to match the SPRITE (symbology.ts normAffil/normDomain rasterises the bitmap from
    // the normalized tuple). Keying the deck texture-dedup id on the RAW feed strings ("FRIEND"
    // vs "friend", "a-f-G-U-C" type vs "ground") over-splits the cache: N ids → 1 bitmap, so the
    // same sprite uploads once per raw variant. Normalizing collapses them to the sprite's own key.
    const aff = normAffil(String(e.properties.affiliation ?? "unknown"));
    const dom = normDomain(this.domainOf(e));
    return `${aff}|${dom}|${this.colorsKey}`;
  }

  /** Milsym markers — affiliation rendered as MIL-STD-2525 / APP-6 FRAME SHAPE via
   *  symbology.ts (DESIGN-SPEC §2.3). This is the channel-law-correct carrier: the
   *  frame geometry carries affiliation so meaning survives a hue strip (the MONO
   *  master test); colour only confirms. Selected gets a white halo ring (the sole
   *  sanctioned selection cue), consistent with the dot path. Sprites are cached by
   *  symbolKey so generation never re-runs per frame. */
  private buildMilsymMarkers(id: string, data: CanonicalEnvelope[]): Layer {
    const colors = this.symbolColors();
    return new IconLayer<CanonicalEnvelope>({
      id: `${id}::milsym`, // mode-distinct (see buildMarkers) so toggling never cross-updates layer classes
      data,
      pickable: true,
      getPosition: (e) => this.renderPoint3(e) ?? [0, 0, 0],
      // Per-object getIcon: symbology returns a self-contained PNG data URL plus its
      // pixel dimensions + centre anchor. The `id` MUST be stable per distinct sprite
      // so deck.gl dedupes the texture upload — symbolKey is exactly that key.
      getIcon: (e) => {
        const aff = e.properties.affiliation ?? "unknown";
        const dom = this.domainOf(e);
        const ic = symbolIcon(aff, dom, colors);
        return {
          url: ic.url,
          width: ic.width,
          height: ic.height,
          anchorX: ic.anchorX,
          anchorY: ic.anchorY,
          id: this.symbolKey(e),
        };
      },
      sizeUnits: "pixels",
      // Constant SCREEN-pixel size (zoom-independent); alerts and the current selection read
      // slightly larger, mirroring the dot path. DECLUTTER: scaled down as you zoom out.
      // ICON-DENSITY LOD: rank-scaled shrink in dense viewports (densitySizeMul).
      getSize: (e) =>
        ((this.isAlert(e) ? 40 : 34) + (e.id === this.selectedId ? 6 : 0)) *
        this.declutter.markerScale *
        this.densitySizeMul(e),
      // CONFIGURABLE FADE on the milsym carrier: a white tint (RGB identity for a full-colour
      // icon — vColorMode 0 keeps texColor.rgb) with a faded ALPHA (the fragment multiplies
      // texColor.a * opacity * vColor.a), so a neutral/unknown frame dims without a hue shift.
      getColor: (e) => [255, 255, 255, Math.round(255 * this.fadeMul(e))],
      // 2.5D-DEPTH: composite in painter's order at tilt (don't depth-sort against the basemap).
      parameters: FLAT_OVERLAY_DEPTH,
      // GPU GLIDE: interpolate the milsym sprite position between data updates (matches the dot).
      // enter:(v)=>v — new sprites enter at target, not null island (see the dot layer note).
      // RE-GATE SNAP: transitions disable for the one membership-change frame (see
      // snapOnNextDataFrame) — else a survivor glides from the removed entity's old index slot.
      transitions: this.snapOnNextDataFrame
        ? {}
        : { getPosition: { duration: this.transitionMs, enter: (v: number[]) => v } },
      updateTriggers: {
        // IVM-3 disjoint triggers. sprite = affiliation / domain (data) × palette (colorsKey).
        // Deliberately keyed on colorsKey — NOT the whole symbologyKey — so a fade-slider drag
        // (which rides getColor's alpha, never the sprite) does not re-rasterise / re-key N sprites.
        getIcon: [this.dataVersion, this.colorsKey],
        getPosition: this.dataVersion,
        // size = isAlert (data) × selection + declutter + density-LOD (interaction).
        getSize: [this.dataVersion, this.interactionKey],
        // white-tint alpha = neutral/unknown fade (symbology) × affiliation (data) × the
        // density-LOD alpha (declutterKey carries the quantized densityFactor).
        getColor: [this.dataVersion, this.symbologyKey, this.declutterKey],
      },
    });
  }

  /** The raw CoT type string ("a-f-G-U-C") the coalition SIDC derives from. Coalition mode
   *  reads properties.type ONLY (the canonical CoT carrier); anything non-string reads as ""
   *  and falls back to the milsym sprite via cotTypeToSidc → null. */
  private cotTypeOf(e: CanonicalEnvelope): string {
    const t = (e.properties as Record<string, unknown>).type;
    return typeof t === "string" ? t : "";
  }

  /** COALITION markers — FULL MIL-STD-2525 symbols rendered by milsymbol from a SIDC derived
   *  off the CoT type (cotTypeToSidc). Per-track fallback: any envelope a SIDC cannot be
   *  derived for (non-atom types, missing/garbled type) — or any milsymbol failure — renders
   *  the built-in milsym frame sprite instead (never blank, never a throw). Sprites are cached
   *  per (sidc | palette) in symbology.ts; the icon `id` mirrors that key so deck dedupes the
   *  texture upload. Only constructed when the milsymbol chunk is READY (rebuildDeck falls
   *  back to the whole ::milsym layer while it loads). */
  private buildCoalitionMarkers(id: string, data: CanonicalEnvelope[]): Layer {
    const colors = this.symbolColors();
    return new IconLayer<CanonicalEnvelope>({
      // Mode-distinct id (LOAD-BEARING — see buildMarkers): deck.gl must never try to
      // cross-update this layer into the ::dot / ::milsym siblings on a mode toggle.
      id: `${id}::coalition`,
      data,
      pickable: true,
      getPosition: (e) => this.renderPoint3(e) ?? [0, 0, 0],
      getIcon: (e) => {
        const aff = e.properties.affiliation ?? "unknown";
        const sidc = cotTypeToSidc(this.cotTypeOf(e));
        const ic = sidc ? coalitionIcon(sidc, colors) : null;
        if (ic) {
          return {
            url: ic.url,
            width: ic.width,
            height: ic.height,
            anchorX: ic.anchorX,
            anchorY: ic.anchorY,
            // Stable per distinct sprite: the (sidc | palette) tuple.
            id: `co|${sidc}|${this.colorsKey}`,
          };
        }
        // FALLBACK: the built-in milsym frame sprite (same key/cache as the ::milsym layer).
        const fb = symbolIcon(aff, this.domainOf(e), colors);
        return {
          url: fb.url,
          width: fb.width,
          height: fb.height,
          anchorX: fb.anchorX,
          anchorY: fb.anchorY,
          id: this.symbolKey(e),
        };
      },
      sizeUnits: "pixels",
      // Sizing mirrors the milsym carrier (isAlert here = hostile-affiliation bump — actual
      // kind:"alert" envelopes never reach this layer; they wear the ::alertglyph instead).
      getSize: (e) =>
        ((this.isAlert(e) ? 40 : 34) + (e.id === this.selectedId ? 6 : 0)) *
        this.declutter.markerScale *
        this.densitySizeMul(e),
      // White tint = RGB identity for the full-colour milsymbol sprite; alpha carries the
      // configurable fade × density-LOD (mirrors the milsym carrier).
      getColor: (e) => [255, 255, 255, Math.round(255 * this.fadeMul(e))],
      parameters: FLAT_OVERLAY_DEPTH,
      // GPU GLIDE + RE-GATE SNAP: identical discipline to the other marker layers — transitions
      // disable for the one membership-change frame, else the position glides (enter-at-target).
      transitions: this.snapOnNextDataFrame
        ? {}
        : { getPosition: { duration: this.transitionMs, enter: (v: number[]) => v } },
      updateTriggers: {
        // sprite = CoT type (data) × palette. colorsKey (not symbologyKey) — a fade-slider drag
        // must not re-rasterise N milsymbol sprites (mirrors the ::milsym reasoning).
        getIcon: [this.dataVersion, this.colorsKey],
        getPosition: this.dataVersion,
        getSize: [this.dataVersion, this.interactionKey],
        getColor: [this.dataVersion, this.symbologyKey, this.declutterKey],
      },
    });
  }

  /** ALERT GLYPHS — kind:"alert" envelopes wear an EVENT glyph, never a unit frame, in every
   *  marker mode (dot → the minimal filled warning-triangle-with-"!"-cutout; milsym/coalition →
   *  the 2525-style activity-event corner-bracket frame + triangle). Affiliation is the COLOR
   *  channel (neutral NWS/USGS alerts stay green-tinted, hostile red); the triangle is the
   *  SHAPE channel ("an event, not a unit"). Drawn ABOVE the marker layer, always pickable
   *  (alerts were pick targets via the marker layer before the split — the pick seam holds).
   *  Honors fadeMul (incl. the density-LOD alpha; alerts rank as hostile), declutter
   *  markerScale, selection/hover emphasis, the RE-GATE SNAP flag and the updateTriggers
   *  discipline like every other marker layer. */
  private buildAlertGlyphs(id: string, data: CanonicalEnvelope[]): Layer {
    const colors = this.symbolColors();
    const mode = this.markerMode;
    const style = mode === "dot" ? "minimal" : "framed";
    return new IconLayer<CanonicalEnvelope>({
      id: `${id}::alertglyph`,
      data,
      pickable: true,
      getPosition: (e) => this.renderPoint3(e) ?? [0, 0, 0],
      getIcon: (e) => {
        const aff = e.properties.affiliation ?? "unknown";
        const ic = alertIcon(style, aff, colors);
        return {
          url: ic.url,
          width: ic.width,
          height: ic.height,
          anchorX: ic.anchorX,
          anchorY: ic.anchorY,
          id: `alert|${style}|${aff}|${this.colorsKey}`,
        };
      },
      sizeUnits: "pixels",
      // KEEP THE ALERT SIZE BUMP: the framed glyph matches the milsym alert size (40); the
      // minimal triangle reads deliberately larger than a plain dot. Emphasis (select/hover)
      // bumps it further; declutter + density-LOD scale it (alerts rank hostile ⇒ gentle).
      getSize: (e) =>
        ((mode === "dot" ? 22 : 40) + (this.isEmphasized(e) ? 6 : 0)) *
        this.declutter.markerScale *
        this.densitySizeMul(e),
      getColor: (e) => [255, 255, 255, Math.round(255 * this.fadeMul(e))],
      parameters: FLAT_OVERLAY_DEPTH,
      // GPU GLIDE + RE-GATE SNAP: same transitions ternary as the other 5 position-tweened layers.
      transitions: this.snapOnNextDataFrame
        ? {}
        : { getPosition: { duration: this.transitionMs, enter: (v: number[]) => v } },
      updateTriggers: {
        // sprite = affiliation (data) × palette × the ACTIVE MODE (this layer keeps one id
        // across modes, so the mode must re-trigger the icon accessor when the style flips).
        getIcon: [this.dataVersion, this.colorsKey, mode],
        getPosition: this.dataVersion,
        getSize: [this.dataVersion, this.interactionKey],
        getColor: [this.dataVersion, this.symbologyKey, this.declutterKey],
      },
    });
  }

  /** Is this envelope a CROWD-tier (OSINT-class, falsifiable) fact? Crowd facts
   *  wear the inner-pip costume; authoritative facts do not. Reads the open
   *  `properties.crowd_pip` flag set by the Living-Net data tagger (hormuz.ts). */
  private isCrowd(e: CanonicalEnvelope): boolean {
    return (e.properties as Record<string, unknown>).crowd_pip === true;
  }

  /** Living-Net CROWD inner-pip COSTUME (DESIGN-SPEC §2 CO-HERO part 2). A small
   *  hollow achromatic ring tagged to the frame's UPPER-RIGHT corner, rendered ONLY
   *  for crowd-tier facts. This is the falsifiable-crowd mark and it rides a channel
   *  that NO owned semantic uses:
   *    - NOT hue        (achromatic stroke — survives the MONO master test)
   *    - NOT opacity    (staleness owns alpha; the pip is full-strength)
   *    - NOT line-dash  (pending-affiliation owns the dash)
   *    - NOT frame shape (affiliation owns the frame; the pip is a CORNER tag, not
   *                       part of the frame, offset away from the centred domain glyph)
   *  Authoritative facts carry no pip — they are the dominant, unmarked, solid tier;
   *  the pip is small + thin so crowd reads as SUBORDINATE, never louder than ground
   *  truth. The sprite is cached (crowdPipSprite) so it never regenerates per frame.
   *  An empty crowd subset yields an empty IconLayer (no cost). */
  private buildCrowdPips(id: string, data: CanonicalEnvelope[]): Layer {
    const crowd = data.filter((e) => this.isCrowd(e));
    const pip = crowdPipSprite();
    return new IconLayer<CanonicalEnvelope>({
      id: `${id}::crowdpip`,
      data: crowd,
      pickable: false, // a costume tag, not a pick target — the frame underneath picks
      getPosition: (e) => this.renderPoint3(e) ?? [0, 0, 0],
      getIcon: () => ({ url: pip.url, width: pip.width, height: pip.height, id: "crowd-pip" }),
      sizeUnits: "pixels",
      getSize: 12,
      // Fixed SCREEN-pixel offset to the frame's upper-right corner so the pip tags
      // the symbol without overlapping the centred domain glyph or the above-frame
      // dimension modifier. Independent of zoom (the frame is screen-sized too).
      getPixelOffset: [13, -13],
      parameters: FLAT_OVERLAY_DEPTH,
      // GPU GLIDE: the crowd-pip tag slides with its frame.
      transitions: this.snapOnNextDataFrame ? {} : { getPosition: this.transitionMs }, // RE-GATE SNAP aware
      updateTriggers: {
        getPosition: this.frameN,
        // Re-key only when the subset changes; the sprite itself is constant.
        getIcon: this.frameN,
      },
    });
  }

  /** The SMEAR — the fragment-shader radial Gaussian uncertainty cloud, rendered
   *  BEHIND the markers (DESIGN-SPEC §2 + §5 build-step 3). Reach + edge-hardness
   *  read CONFIDENCE; peak alpha reads STALENESS; the two never cross (the channel
   *  law, enforced both here in smearGeometry and in the GLSL). The cloud is a
   *  WHISPER at rest (faint ambient peak) tinted with the track's AFFILIATION hue
   *  (so it reads as that track's uncertainty — red hostile, green friend); it
   *  BLOOMS only when its track is selected or hovered (reveal-on-focus). Meaning is
   *  still alpha + reach; hue CONFIRMS ownership (the frame carries affiliation by
   *  SHAPE for MONO). Only track/observation kinds get a cloud; everything else
   *  passes through empty. */
  private buildSmear(
    id: string,
    data: CanonicalEnvelope[],
    nowMs: number,
  ): Layer {
    // FOCUS-ONLY: the resting map shows NO clouds at all — only the hovered/selected
    // track gets a smear. Filtering to emphasized tracks means zero instances (zero
    // GPU) at rest; a cloud appears solely for the one track in focus.
    const smearData = data.filter((e) => hasSmear(e) && this.isEmphasized(e));
    // OVERLAP DETUNE: where multiple clouds pile up, the additive blend AMPLIFIES into a
    // hard blob. Compute each cloud's fractional footprint overlap with its neighbours and
    // recede its opacity proportionally (~90% overlap -> ~10% opacity) so a pile stays a calm
    // haze and the iconography on top keeps the emphasis. Project each cloud (lng/lat + reach
    // METRES) into a shared local-metre plane so the pure circle-overlap math is in one unit.
    // PRESENTATION ONLY — this scales ALPHA, never reach/sigma (CONFIDENCE) or WHAT renders.
    const overlapMul = this.smearOverlapMultipliers(smearData, nowMs);
    return new GaussianCloudLayer<CanonicalEnvelope>({
      // Mode-distinct sub-layer id (mirrors ::milsym / ::dot / ::halo) so deck.gl
      // never cross-updates this custom layer class into a sibling on rebuild.
      id: `${id}::smear`,
      data: smearData,
      pickable: false,
      stroked: false,
      filled: true,
      antialiasing: true,
      // reach = the cloud's geographic radius in METRES => it zooms WITH the map
      // (a real area on the ground, not a fixed pixel blob). CONFIDENCE channel.
      radiusUnits: "meters",
      radiusMinPixels: 6, // a tight high-confidence cloud is still a visible soft dot
      radiusMaxPixels: 600,
      // 2.5D CO-LOCATION: the cloud sits at the item's FULL position — same lat/lon
      // AND same altitude (point3Of -> zOf) the marker/icon/halo use — so the cloud
      // hugs the lifted glyph instead of detaching at sea level (deck z=0 footgun).
      getPosition: (e) => this.renderPoint3(e) ?? [0, 0, 0],
      // CONFIDENCE -> reach (metres). On EMPHASIS (select/hover) the focused cloud
      // breathes outward a touch (bloomReachMult) — an interaction overlay, not a
      // confidence change; the resting reach is unchanged.
      getRadius: (e) => {
        const r = smearGeometry(e, nowMs).reachMeters;
        return this.isEmphasized(e) ? r * SMEAR.bloomReachMult : r;
      },
      // CONFIDENCE -> sigma (Gaussian width as a fraction of reach): crisp tight core
      // for high conf, wide soft falloff for low conf. This is the CROSS-track sigma; the
      // along-track axis is stretched by getAspect below. NEVER touches alpha.
      getSigma: (e) => smearGeometry(e, nowMs).sigma,
      // VELOCITY-CORRELATED ELLIPSE (motion-ellipse): along/cross aspect ratio (>=1). A static
      // track is 1 (isotropic — identical to the legacy radial cloud); a mover ELONGATES along
      // heading, scaled by speed × the per-domain assumption (tight ships / wide unknown-hostile
      // air, from the advanced-tab ellipse settings). EXTENT only — alpha is untouched.
      getAspect: (e) =>
        ellipsePlan({
          baseSigma: smearGeometry(e, nowMs).sigma,
          headingDeg: numProp(e, "heading_deg") ?? 0,
          speedKn: numProp(e, "speed_kn") ?? 0,
          domainStretch: ellipseStretchFor(e, this.renderSettings),
        }).aspect,
      // The ellipse long-axis bearing — the cloud's elongation rotates to the track's heading.
      getHeading: (e) => numProp(e, "heading_deg") ?? 0,
      // FULLY INVISIBLE AT REST: peak alpha is 0 unless the track is emphasized
      // (defensive — the data is already filtered to emphasized, but this guarantees
      // it even if an unfocused track ever reaches the accessor). On EMPHASIS
      // (select/hover) ITS smear BLOOMS: the age-encoded peak is multiplied up
      // (capped) so the focused track's uncertainty reveals on focus. Emphasis is an
      // interaction-state overlay (like the marker size-bump); it does not re-encode age.
      getPeak: (e) => {
        if (!this.isEmphasized(e)) return 0; // no cloud at rest
        const base = smearGeometry(e, nowMs).peakAlpha; // age-encoded bloom seed
        const bloomed = Math.min(SMEAR.bloomPeakCap, base * SMEAR.bloomPeakMult);
        // OVERLAP DETUNE: recede a cloud buried under its neighbours (alpha only).
        return bloomed * (overlapMul.get(e.id) ?? 1);
      },
      // AFFILIATION-tinted cloud body: the SAME affiliation hue the icon/frame uses
      // (affRgb, single-sourced from affiliationColors) so a hostile track's
      // uncertainty reads RED, a friend's GREEN, etc. The shader multiplies this RGB
      // by the per-fragment Gaussian alpha; MEANING is still alpha + reach, hue
      // CONFIRMS which track owns the cloud. (Survives MONO via the frame, which
      // carries affiliation by SHAPE; the hue here is a confirming channel.)
      getFillColor: (e) => this.affRgb(e),
      // Soft additive compositing: overlapping clouds GLOW together into one calm
      // haze instead of stacking into a hard thicket. [SRC_ALPHA, ONE | ONE,
      // ONE_MINUS_SRC_ALPHA], FUNC_ADD. depthTest off so the cloud always sits flat
      // behind the markers.
      parameters: {
        // depth test off (luma.gl v9: always-pass + no depth write) so the cloud
        // sits flat behind the markers and never z-fights the basemap.
        depthCompare: "always",
        depthWriteEnabled: false,
        blendColorSrcFactor: "src-alpha",
        blendColorDstFactor: "one",
        blendAlphaSrcFactor: "one",
        blendAlphaDstFactor: "one-minus-src-alpha",
        blendColorOperation: "add",
        blendAlphaOperation: "add",
      },
      updateTriggers: {
        // reach + peak depend on emphasis (select/hover); frameN already changes per
        // rebuild, the ids are belt-and-suspenders so the bloom is never missed.
        getRadius: [this.frameN, this.selectedId, this.hoveredId],
        getSigma: this.frameN,
        // ellipse aspect/heading re-resolve per rebuild (motion) + on a settings change (frameN).
        getAspect: this.frameN,
        getHeading: this.frameN,
        getPeak: [this.frameN, this.selectedId, this.hoveredId],
        getPosition: this.frameN,
        // affiliation hue re-resolves when a theme swap re-keys the palette (frameN
        // increments on setAffiliationColors -> rebuildDeck).
        getFillColor: this.frameN,
      },
    });
  }

  /** Per-cloud OVERLAP-DETUNE opacity multiplier for the smear set. Projects each cloud
   *  (lng/lat + reach METRES) into a shared local-metre plane via an equirectangular scale
   *  about the set's mean latitude, then defers to the pure smearOverlap math (circle-overlap
   *  fraction -> opacity ~= 1 - coverage). The drawn reach MIRRORS getRadius (an emphasized
   *  cloud carries bloomReachMult). Empty / single-cloud sets are all 1 (nothing to detune).
   *  PRESENTATION ONLY: feeds getPeak (alpha) — never reach/sigma, never WHAT renders. */
  private smearOverlapMultipliers(
    smearData: CanonicalEnvelope[],
    nowMs: number,
  ): Map<string, number> {
    const out = new Map<string, number>();
    if (smearData.length <= 1) {
      for (const e of smearData) out.set(e.id, 1);
      return out;
    }
    // Reference latitude for the equirectangular lng->metre scale (shared by every disc so
    // the circle-overlap math sees consistent units).
    let latSum = 0;
    let nLat = 0;
    for (const e of smearData) {
      const p = pointOf(e);
      if (p) {
        latSum += p[1];
        nLat++;
      }
    }
    const refLat = nLat > 0 ? latSum / nLat : 0;
    const M_PER_DEG_LAT = 111_320;
    const mPerDegLng = M_PER_DEG_LAT * Math.cos((refLat * Math.PI) / 180);
    const discs = smearData.map((e) => {
      const p = pointOf(e) ?? [0, 0];
      const reachBase = smearGeometry(e, nowMs).reachMeters;
      const reach = this.isEmphasized(e) ? reachBase * SMEAR.bloomReachMult : reachBase;
      return { x: p[0] * mPerDegLng, y: p[1] * M_PER_DEG_LAT, r: reach };
    });
    const muls = smearOpacityMultipliers(discs);
    smearData.forEach((e, i) => out.set(e.id, muls[i]));
    return out;
  }

  /** The soft-glow halo's SCREEN-PIXEL radius for one entity. Single-sourced so the rendered
   *  {@link buildHalo} disc and the overlap-detune footprint ({@link computeHaloDetune}) agree.
   *  2.5D-DEPTH: grows with altitude, STILL CAPPED below the isAlert delta (+5px) so an alert
   *  halo always out-grows a benign high-flyer (altitude adds at most +4.5px). */
  private haloRadius(e: CanonicalEnvelope): number {
    return (this.isAlert(e) ? tuning.haloPx + 5 : tuning.haloPx) + 4.5 * altNorm(zOf(e));
  }

  /** GREEN-DECLUTTER: recompute {@link haloDetune} — the per-entity halo opacity multiplier — from
   *  the current frame. Each rendered marker's halo is a SCREEN-PIXEL disc (centre = projected
   *  lng/lat, radius = {@link haloRadius}); where many overlap they additively pile into a green
   *  wash, so the pure overlap helper recedes the opacity of buried halos (high overlap ⇒ low
   *  alpha) while the crisp marker iconography on top is left untouched. Computed across ALL
   *  rendered specs so a cross-spec pile is detuned too.
   *
   *  FAIL-SAFE: if the map can't project (headless / stub), leave the map empty ⇒ every lookup
   *  falls back to 1 (no detune) — the detune can only ever DIM an additive pile-up, never
   *  brighten. PRESENTATION ONLY (I13): alpha only; never changes WHAT renders. */
  private computeHaloDetune(): void {
    this.haloDetune.clear();
    const map = this.map as unknown as {
      project?: (lngLat: [number, number]) => { x: number; y: number };
    };
    const project = typeof map.project === "function" ? map.project.bind(this.map) : null;
    if (!project) return; // no projection available -> no detune (fail-safe to full opacity)

    const ids: string[] = [];
    const discs: SmearDisc[] = [];
    for (const [id] of this.specs) {
      const data = this.frameData.get(id) ?? [];
      for (const e of data) {
        const ll = pointOf(e);
        if (!ll) continue;
        let pt: { x: number; y: number };
        try {
          pt = project(ll);
        } catch {
          continue; // a projection throw on one point never poisons the whole frame
        }
        if (!pt || !Number.isFinite(pt.x) || !Number.isFinite(pt.y)) continue;
        ids.push(e.id);
        discs.push({ x: pt.x, y: pt.y, r: this.haloRadius(e) });
      }
    }
    const mult = haloOpacityMultipliers(discs);
    for (let i = 0; i < ids.length; i++) this.haloDetune.set(ids[i], mult[i]);
  }

  /** Soft glow behind each marker — gives the picture depth on the dark scope. */
  private buildHalo(id: string, data: CanonicalEnvelope[]): Layer {
    return new ScatterplotLayer<CanonicalEnvelope>({
      id: `${id}::halo`,
      data,
      pickable: false,
      stroked: false,
      filled: true,
      radiusUnits: "pixels",
      getPosition: (e) => this.renderPoint3(e) ?? [0, 0, 0],
      // 2.5D-DEPTH: the halo glow brightens + grows with altitude — a 2D-legible,
      // mode-agnostic altitude cue (reads under milsym frames AND dots). Nudged up a
      // touch for drama, but STILL CAPPED below the isAlert delta (+5px / +28a) so an
      // alert halo always out-glows a benign high-flyer: altitude adds at most
      // +4.5px / +27a, keeping alert salience strictly dominant (27 < 28, 4.5 < 5).
      getRadius: (e) => this.haloRadius(e),
      getFillColor: (e) => {
        const [r, g, b] = this.affRgb(e);
        const base = this.isAlert(e) ? 70 : 42;
        // CONFIGURABLE FADE: a neutral/unknown halo dims with its marker (clutter cut).
        // GREEN-DECLUTTER: a halo buried under overlapping neighbours recedes by its local
        // overlap fraction (computeHaloDetune) so a dense cluster stops amplifying into a green
        // wash — the iconography stays crisp. Absent id ⇒ 1 (no detune). ALPHA only (I13).
        const a =
          (base + Math.round(27 * altNorm(zOf(e)))) *
          this.fadeMul(e) *
          (this.haloDetune.get(e.id) ?? 1);
        return [r, g, b, Math.round(a)];
      },
      parameters: FLAT_OVERLAY_DEPTH,
      // GPU GLIDE: the halo slides with its marker (same transition duration).
      transitions: this.snapOnNextDataFrame ? {} : { getPosition: this.transitionMs }, // RE-GATE SNAP aware
      updateTriggers: {
        // IVM-3: the fill folds in the GREEN-DECLUTTER halo detune, which is recomputed from live
        // SCREEN geometry every rebuild (zoom-sensitive, sub-bucket), so it stays keyed on the
        // per-rebuild epoch (frameN) — the fill accessor is cheap arithmetic and re-running it does
        // NOT restart the position transition. radius (isAlert + altitude) + position are DATA-only,
        // so a hover / select / zoom never re-uploads them and the glide is undisturbed.
        getFillColor: this.frameN,
        getRadius: this.dataVersion,
        getPosition: this.dataVersion,
      },
    });
  }

  /** Is this an air/UAS domain track? (drop-lines are an air-picture cue). */
  private isAirDomain(e: CanonicalEnvelope): boolean {
    const p = e.properties as Record<string, unknown>;
    const d = typeof p.domain === "string" ? p.domain.toLowerCase() : "";
    return d === "air" || d === "uas" || d === "uav";
  }

  /** 2.5D-DEPTH: vertical drop-line(s) from the surface up to a lifted air/UAS glyph, PLUS a small
   *  dim ground dot at the base. Achromatic scaffolding (SCAFFOLD_TINT) so it never reads as a
   *  second affiliation track. At pitch 0 (2D) the stalk has zero screen extent (source + target
   *  share x/y), so it is inert in the flat scope and blooms only on tilt; the ground dot still
   *  marks the over-ground position in 2D.
   *
   *  GATING (declutter, per Decisions log — NOT always-on): a track gets a drop-line when it is
   *    (a) EMPHASIZED (hover/select — full detail on focus, any domain above the surface), OR
   *    (b) AIR/UAS AND zoomed in past dropLine.minZoom AND above dropLine.minAltitudeM.
   *  So the resting wide-angle scope stays calm; drop-lines reveal for the air picture once you
   *  zoom in, and for any focused track. Returns [groundDot, stalkLine] (both empty ⇒ no cost). */
  private buildDropLines(id: string, data: CanonicalEnvelope[], zoom: number): Layer[] {
    type Stalk = { lng: number; lat: number; z: number; base: number };
    const dl = this.renderSettings.advanced.dropLine;
    const stalks: Stalk[] = [];
    for (const e of data) {
      const z = zOf(e);
      if (z <= 1) continue; // on the surface — nothing to drop
      const gated =
        this.isEmphasized(e) ||
        (this.isAirDomain(e) && zoom >= dl.minZoom && z >= dl.minAltitudeM);
      if (!gated) continue;
      const p = pointOf(e);
      if (!p) continue;
      // 2.5D-DEPTH P4: anchor the stalk base on the TERRAIN surface when terrain is active —
      // deck z=0 is SEA level, not the terrain, so without this the drop-line would start below
      // the ground over high relief. GUARDED: queryTerrainElevation throws when no terrain is
      // set (this.terrain is null in MapLibre v5), and this runs inside the INTERLEAVED render
      // pass — an uncaught throw blanks the WHOLE canvas. Terrain off -> base 0 = sea level.
      let base = 0;
      try {
        const terr = this.map.getTerrain?.() as { exaggeration?: number } | null;
        if (terr) {
          const exag = terr.exaggeration && terr.exaggeration > 0 ? terr.exaggeration : 1;
          base = (this.map.queryTerrainElevation([p[0], p[1]]) ?? 0) / exag;
        }
      } catch {
        base = 0;
      }
      if (!Number.isFinite(base)) base = 0; // a NaN terrain reading must not streak the stalk
      // p[0]/p[1] are finite (pointOf finite-guarded) and the stalk runs straight up from the
      // TRUE ground point directly below the glyph; z is clampAltM-bounded (zOf), so the stalk is
      // a thin, height-bounded vertical — never a screen-tall bar to a garbage anchor/altitude.
      stalks.push({ lng: p[0], lat: p[1], z, base });
    }
    const [sr, sg, sb] = SCAFFOLD_TINT;
    const lineAlpha = Math.round(255 * dl.lineOpacity);
    const dotAlpha = Math.round(255 * dl.groundDotOpacity);
    const stalkLine = new LineLayer<Stalk>({
      id: `${id}::stalk`,
      data: stalks,
      pickable: false,
      widthUnits: "pixels",
      widthMinPixels: 1,
      getWidth: 1,
      getSourcePosition: (d) => [d.lng, d.lat, d.base],
      getTargetPosition: (d) => [d.lng, d.lat, d.z],
      getColor: [sr, sg, sb, lineAlpha],
      parameters: FLAT_OVERLAY_DEPTH,
      updateTriggers: {
        getSourcePosition: this.frameN,
        getTargetPosition: this.frameN,
        // IVM-3 wrong-semantic fix: the stalk colour is [SCAFFOLD_TINT, 255·dropLine.lineOpacity]
        // — it depends on the drop-line OPACITY SETTING, not the zoom-declutter bucket. Keyed on
        // symbologyKey so dragging the drop-line opacity slider re-resolves it (declutterKey would
        // have left it stale once the stalk data is memoised).
        getColor: this.symbologyKey,
      },
    });
    // Small dim ground dot at the base (~20% of the main marker diameter, ~30% opacity) — marks
    // where the air track sits over the ground without inventing a second affiliation contact.
    const groundDot = new ScatterplotLayer<Stalk>({
      id: `${id}::dropdot`,
      data: stalks,
      pickable: false,
      stroked: false,
      filled: true,
      radiusUnits: "pixels",
      radiusMinPixels: 1,
      getPosition: (d) => [d.lng, d.lat, d.base],
      getRadius: tuning.markerPx * 0.2 * this.declutter.markerScale,
      getFillColor: [sr, sg, sb, dotAlpha],
      parameters: FLAT_OVERLAY_DEPTH,
      updateTriggers: {
        getPosition: this.frameN,
        // radius = markerPx·0.2·markerScale — genuinely declutter-dependent (keep declutterKey).
        getRadius: this.declutterKey,
        // IVM-3 wrong-semantic fix: the ground-dot fill is [SCAFFOLD_TINT, 255·dropLine.groundDot
        // Opacity] — a drop-line OPACITY SETTING, not the declutter bucket. Keyed on symbologyKey.
        getFillColor: this.symbologyKey,
      },
    });
    return [groundDot, stalkLine];
  }

  /** Velocity leader lines — a line from each MOVING track along its heading, length ∝ speed.
   *  LIVENESS: a leader reads "this is going somewhere", so genuine movers carry a faint
   *  always-on leader and the resting picture shows the live vector field — NOT a static dot
   *  field. DECLUTTER (channel-budget respected): the resting leader shares the CALLSIGN
   *  declutter gate (declutter.labelsVisible) — when the view is too dense for labels it is too
   *  dense for leaders, so they collapse to FOCUS-ONLY (hover/select) and the wide-angle scope
   *  stays calm. Static/anchored tracks (speed < LEADER_MIN_KN) never draw one. Emphasis (focus)
   *  always draws + brightens regardless of density. Source reads renderPoint3 so the leader
   *  GLIDES with its marker (the single-position chokepoint, per renderPoint3's contract). */
  private buildHeading(id: string, data: CanonicalEnvelope[]): Layer {
    type Seg = { source: [number, number, number]; target: [number, number, number]; color: RGBA };
    /** Below this speed a track is treated as static — no leader (anchored ships, parked air). */
    const LEADER_MIN_KN = 5;
    const RESTING_LEADER_ALPHA = 48; // a faint always-on whisper (≈19%) — the live vector field
    const FOCUS_LEADER_ALPHA = 150; // hover/select bumps the leader forward (≈59%)
    const restingVisible = this.declutter.labelsVisible; // calm enough for labels ⇒ calm enough for leaders
    const segs: Seg[] = [];
    for (const e of data) {
      const emphasized = this.isEmphasized(e);
      // FOCUS-ONLY fallback in dense views: a resting leader needs the calm (label-visible) gate;
      // an emphasized track always shows one.
      if (!emphasized && !restingVisible) continue;
      const p = this.renderPoint3(e); // tweened position → the leader glides with the marker
      const hdg = numProp(e, "heading_deg");
      const spd = numProp(e, "speed_kn") ?? 0;
      if (!p || hdg === null || spd < LEADER_MIN_KN) continue; // movers only
      const [r, g, b] = this.affRgb(e);
      // 2.5D-DEPTH: leader emanates from the lifted glyph at its altitude (target shares z).
      // VECTOR SCALE: the user multiplier (advanced.vectorScale) lengthens/shortens the leader.
      const tgt = leaderTarget(p[0], p[1], hdg, spd, this.renderSettings.advanced.vectorScale);
      if (!isFiniteLngLat(tgt)) continue; // never project to a garbage endpoint
      // SUBTLE velocity cue: a thin, transparent leader — a lightweight heading/speed hint, NOT a
      // bold laser. The resting whisper stays well behind the iconography; focus brightens it.
      const alpha = emphasized ? FOCUS_LEADER_ALPHA : RESTING_LEADER_ALPHA;
      segs.push({ source: p, target: [tgt[0], tgt[1], p[2]], color: [r, g, b, alpha] });
    }
    return new LineLayer<Seg>({
      id: `${id}::heading`,
      data: segs,
      pickable: false,
      widthUnits: "pixels",
      widthMinPixels: 0.75,
      getWidth: 1,
      getSourcePosition: (d) => d.source,
      getTargetPosition: (d) => d.target,
      getColor: (d) => d.color,
      parameters: FLAT_OVERLAY_DEPTH,
      // GPU GLIDE: the leader's endpoints slide with the marker (both ends transition together).
      transitions: this.snapOnNextDataFrame
        ? {}
        : { getSourcePosition: this.transitionMs, getTargetPosition: this.transitionMs }, // RE-GATE SNAP aware
      updateTriggers: { getSourcePosition: this.frameN, getTargetPosition: this.frameN, getColor: this.frameN },
    });
  }

  /** COMET-TAIL motion trails (rank-1 visual upgrade) — each visible track's recent
   *  position history rendered as a per-SEGMENT gradient that DECAYS into the past:
   *  a crisp, wide head at the live position fading to a faint, thin oldest tail.
   *
   *  Why a LineLayer of segments and not a PathLayer: deck.gl's PathLayer getColor /
   *  getWidth are per-PATH accessors, so they cannot ramp colour/width ALONG a single
   *  track. Splitting each track into its segments lets every segment carry its own
   *  ramp value (trailRamp(tuning.trailLen, tuning.trailHeadPx), keyed by steps-from-head)
   *  — the comet decay.
   *
   *  PERF: the trail history only deepens for tracks whose position actually CHANGED
   *  (writeFrame appends on move only), so STATIC tracks contribute ZERO segments —
   *  the segment count is bounded by (moving tracks × <=tuning.trailLen), well under the 5k
   *  worst case. Colour comes from the SAME visible `data` the markers use, so trails
   *  stay clearance-correct (only visible tracks leave a tail). Altitude-tinted +
   *  z-aware (segments ride each stored position's altitude, so the tail stays
   *  attached to the lifted glyph at tilt).
   *
   *  CONTRAIL DECAY: each segment's style is the MINIMUM of the index ramp (steps-from-head)
   *  and an AGE decay against the segment's recorded wall-clock time — old vapor thins and
   *  vanishes (alpha 1−ageFrac, width √(1−ageFrac) toward tuning.trailMaxAgeS) even on a track
   *  that stopped updating. LENGTH DEMOTION (defense-in-depth for throttled-rAF windows, where
   *  writeFrame still runs occasionally so the time-gap break may not trip): a segment longer
   *  than tuning.trailLongSegNm renders at the faint TAIL styling, never as a bright head —
   *  an outsized step can never read as a crisp flight path. `nowMs` is rebuildDeck's single
   *  per-rebuild Date.now(), the SAME timeline writeFrame stamps the points with. */
  private buildTrails(id: string, data: CanonicalEnvelope[], nowMs: number): Layer {
    type Seg = {
      source: [number, number, number];
      target: [number, number, number];
      color: RGBA;
      width: number;
    };
    const segs: Seg[] = [];
    const ramp = trailRamp(tuning.trailLen, tuning.trailHeadPx);
    const tailIdx = ramp.length - 1;
    const maxAgeMs = tuning.trailMaxAgeS * 1000;
    const longSegNm = tuning.trailLongSegNm;
    // VECTOR/TAIL SCALE: the user multiplier (advanced.vectorScale) sets how much of the stored
    // history renders as the comet — a longer tail when >1 (capped at the stored tuning.trailLen),
    // a shorter stub when <1. Always ≥2 segments so a tail never vanishes entirely.
    const vscale = this.renderSettings.advanced.vectorScale;
    const trailLen = tuning.trailLen;
    const maxSeg = Math.max(2, Math.min(trailLen, Math.round(trailLen * (vscale > 0 ? vscale : 1))));
    for (const e of data) {
      const hist = this.trails.get(e.id);
      if (!hist || hist.length < 2) continue;
      // One altitude tint per track (head altitude), then the per-segment alpha ramp.
      const [r, g, b] = altTint(this.affRgb(e), zOf(e));
      const L = hist.length;
      const jStart = Math.max(0, L - 1 - maxSeg); // render only the most-recent maxSeg segments
      for (let j = jStart; j < L - 1; j++) {
        // DEFENSE-IN-DEPTH: never draw a segment across a non-finite or teleport gap (segmentNm is
        // +Infinity for non-finite endpoints). writeFrame already breaks the buffer on a teleport,
        // so this normally never trips — it just guarantees no cross-screen comet segment ever.
        const nm = segmentNm(hist[j].p, hist[j + 1].p);
        if (nm > MAX_RENDER_SEG_NM) continue;
        // AGE DECAY (contrail): fully-aged vapor is invisible — skip the vertices entirely. Aged
        // against the NEWER endpoint (the segment "formed" when its later sample was recorded).
        const ageFrac = Math.min(1, Math.max(0, (nowMs - hist[j + 1].t) / maxAgeMs));
        if (ageFrac >= 1) continue;
        const alphaMul = 1 - ageFrac;
        const widthMul = Math.sqrt(alphaMul);
        // steps back from the head: 0 = the segment ending at the live position. LENGTH
        // DEMOTION: an outsized (yet sub-teleport) segment wears the faint tail style instead.
        const stepsFromHead = L - 2 - j;
        const seg =
          nm > longSegNm ? ramp[tailIdx] : ramp[stepsFromHead < tailIdx ? stepsFromHead : tailIdx];
        segs.push({
          source: hist[j].p,
          target: hist[j + 1].p,
          color: [r, g, b, Math.min(seg.alpha, Math.round(TRAIL_HEAD_ALPHA * alphaMul))],
          width: Math.min(seg.width, tuning.trailHeadPx * widthMul),
        });
      }
    }
    return new LineLayer<Seg>({
      id: `${id}::trail`,
      data: segs,
      pickable: false,
      widthUnits: "pixels",
      widthMinPixels: 0.8,
      getWidth: (d) => d.width,
      getSourcePosition: (d) => d.source,
      getTargetPosition: (d) => d.target,
      getColor: (d) => d.color,
      parameters: FLAT_OVERLAY_DEPTH,
      updateTriggers: {
        getSourcePosition: this.frameN,
        getTargetPosition: this.frameN,
        getColor: this.frameN,
        getWidth: this.frameN,
      },
    });
  }

  /** DEAD-RECKONED FUTURE PATH (rank-2 visual upgrade) — a thin, desaturated, FADING
   *  forward projection ahead of every track that carries the SHARED CROSS-LANE
   *  contract `properties.future_path` ([lon, lat, altMetres][], up to 8 points at
   *  +30s..+240s from the deterministic motion model; written by L-DATA). The path is
   *  anchored at the live position so it grows OUT of the marker. Rendered BEHIND the
   *  marker (inserted before it in rebuildDeck) as a calm echo: the affiliation hue
   *  desaturated to ~40%, split into per-segment items (mirror of the comet-tail) so
   *  alpha + width DECAY INTO THE FUTURE (FUTURE_RAMP) — a modest near-marker head
   *  fading to a near-0 tip, so a projection reads as "where it's heading, less
   *  certain the further out" and a fast track never draws a solid spear across the
   *  scope. deck's PathLayer getColor/getWidth are per-PATH, so each segment is fed as
   *  its own 2-point path to carry its own ramp value.
   *
   *  VECTOR/TAIL SCALE: the SAME user multiplier the comet-tail uses
   *  (advanced.vectorScale) gates how far forward the forecast reaches — maxSeg
   *  forward segments out of FUTURE_LEN. Unlike the trail (which never fully
   *  vanishes), the future cue is allowed to disappear entirely: at vectorScale 0
   *  no projection draws; at 1.0 the full +240s horizon renders; the default 0.4
   *  shows the near forecast only.
   *
   *  Absent / malformed future_path (e.g. live ADS-B with no motion model) → that
   *  track simply contributes no segments (futurePathOf returns null). 2.5D-aware: the
   *  projected positions carry their own altitude, so the line lifts with the track at
   *  tilt. Same visible `data` as the markers → clearance-correct. */
  private buildFuturePath(id: string, data: CanonicalEnvelope[]): Layer {
    type FSeg = { path: [number, number, number][]; color: RGBA; width: number };
    const segs: FSeg[] = [];
    const tipIdx = FUTURE_RAMP.length - 1;
    // VECTOR/TAIL SCALE — see doc comment. round(FUTURE_LEN * vscale) forward segments; 0 at
    // vectorScale 0 (the cue disappears), clamped to the full FUTURE_LEN horizon at >=1.0.
    const vscale = this.renderSettings.advanced.vectorScale;
    const maxSeg = Math.max(0, Math.min(FUTURE_LEN, Math.round(FUTURE_LEN * vscale)));
    if (maxSeg >= 1) {
      for (const e of data) {
        const fp = futurePathOf(e);
        if (!fp) continue;
        const head = point3Of(e);
        const raw = head ? [head, ...fp] : fp;
        // TRUNCATE-AT-WRAP: cut the projection at the first non-finite point or teleport (a fast
        // track whose +30s..+240s window crosses its duration_s wrap snaps ~200nm back to start —
        // un-truncated that drew a crisp line straight across the scope). continuousRun keeps the
        // leading continuous run from the live head forward, so the path reads as a real forecast.
        const path = continuousRun(raw);
        if (path.length < 2) continue; // need a head + >=1 continuous projected point to draw a line
        const [r, g, b] = desaturate(this.affRgb(e), 0.4);
        // FORWARD FADE: segment j runs path[j]->path[j+1]; j=0 is the near-marker head (brightest),
        // decaying toward the faint tip via FUTURE_RAMP. Cap the rendered forward segments at maxSeg
        // (vector-scale) AND at the track's own available projected length.
        const last = Math.min(path.length - 1, maxSeg);
        for (let j = 0; j < last; j++) {
          const ramp = FUTURE_RAMP[j < tipIdx ? j : tipIdx];
          segs.push({ path: [path[j], path[j + 1]], color: [r, g, b, ramp.alpha], width: ramp.width });
        }
      }
    }
    return new PathLayer<FSeg>({
      id: `${id}::future`,
      data: segs,
      pickable: false,
      widthUnits: "pixels",
      widthMinPixels: 0.6,
      getWidth: (d) => d.width,
      capRounded: true,
      jointRounded: true,
      getPath: (d) => d.path,
      getColor: (d) => d.color,
      parameters: FLAT_OVERLAY_DEPTH,
      updateTriggers: { getPath: this.frameN, getColor: this.frameN, getWidth: this.frameN },
    });
  }

  /** The track's callsign for the label. Prefers `properties.callsign` (ADS-B + demo scenarios +
   *  the live gateway all set it first-class now — cot.ts files the CoT contact callsign there);
   *  falls back CLIENT-SIDE to `properties.name` for tracks whose author gave a name but no
   *  callsign (third-party feeds; corpus haystack friendlies). Guarded against a raw machine UID
   *  (a gateway track with no contact element carries name=uid), which would render as noise.
   *  Long text TRUNCATES with an ellipsis via labelText (never dropped for length) and reveals
   *  in FULL while the track is emphasized (hover/tap). Returns null when nothing is label-worthy. */
  private callsignOf(e: CanonicalEnvelope): string | null {
    const props = e.properties as Record<string, unknown>;
    const c = props.callsign;
    if (typeof c === "string" && c.trim().length > 0) return this.labelText(c.trim(), e);
    // NAME-FALLBACK GATE (DESIGN-CLUTTER-PRIORITY v2 §1.2 — the label flood's biggest lever):
    // by AFFILIATION + KIND + VIEW DENSITY + EMPHASIS, NEVER by feed. In a NORMAL (dense) view
    // the fallback opens only to TACTICALLY-SIGNIFICANT tracks (friend/hostile), ALERTS
    // (kind === "alert" — real alert feeds like NWS/USGS arrive NEUTRAL and callsign-less, so
    // without this seam a tornado warning could never label), and the track the operator is
    // pointing at (emphasized). What it must exclude is the bulk NEUTRAL/UNKNOWN haystack —
    // named vessels label only when sparse, hovered, or selected: names stay one interaction
    // away ("clean but complete"; the density cull + tuning.labelDensityKeep bound the rest).
    // In a SPARSE view the wall the gate prevents cannot exist, so it opens to EVERY
    // affiliation — a lone neutral weather alert / AIS vessel on screen gets its name.
    // (History: a source === "tak/live" opener shipped at the real-TAK cutover to fix the LOCAL
    // haystack — but on prod EVERY entity is tak/live, so the gate was a NO-OP there and every
    // named neutral vessel labelled at zoom ≥7: Will's Hormuz label-flood screenshot. REMOVED —
    // the gate is now feed-agnostic. Earlier, a source-ALLOWLIST dropped every scenario/local
    // blue-force name — the NIGHT-3c regression. Neither direction of feed-gating survives.)
    // UID-guarded so a track whose name is just its machine uid stays unlabeled.
    const aff = props.affiliation;
    if (nameFallbackOpens(this.sparseView, this.isEmphasized(e), aff, e.kind)) {
      const n = props.name;
      if (typeof n === "string") {
        const t = n.trim();
        if (t.length > 0 && !looksLikeUid(t)) return this.labelText(t, e);
      }
    }
    return null;
  }

  /** Cap a label's length by TRUNCATING with an ellipsis — never by dropping the label ("we have
   *  real estate; refining how we use it is worth it"). The cap is view-density-aware
   *  (tuning.labelNameMax dense / tuning.sparseLabelNameMax sparse — alert headlines fit when
   *  room exists) and an EMPHASIZED (hovered/tapped/selected) track always reveals its FULL
   *  text — truncation is a resting-state economy, not a data limit. Safe under the getText
   *  frameN updateTrigger: hover/select bumps a rebuild, so the reveal re-evaluates. */
  private labelText(t: string, e: CanonicalEnvelope): string {
    if (this.isEmphasized(e)) return t;
    const cap = Math.max(4, this.sparseView ? tuning.sparseLabelNameMax : tuning.labelNameMax);
    return t.length <= cap ? t : `${t.slice(0, cap - 1).trimEnd()}…`;
  }

  /** Per-entity BASE label offset (px): the vertical distance from the marker's anchor to the
   *  label's top edge — HALF the marker's RENDERED extent (mirroring the marker layers' size
   *  formulas exactly: per-mode base × alert/selection bumps × declutter markerScale ×
   *  density-LOD shrink) + a constant breathing gap (LABEL_GAP_PX; the plate's
   *  backgroundPadding eats ~2px of it). REPLACES the old per-mode fixed constant
   *  (12 dot / 22 sym × markerScale), which only tracked the zoom declutter: a selected or
   *  alert-bumped milsym frame renders up to 46px tall (half-extent 23 > 22), so the icon
   *  painted over its own label's plate — and a density-LOD-shrunk icon left its label
   *  floating. Anchoring to the drawn half-extent keeps the label just BELOW the marker edge
   *  at every size. Default parity: milsym 34/2+6 ≈ 23 (was 22), dot 5.5+6 ≈ 12 (was 12). */
  private labelBelowPx(e: CanonicalEnvelope): number {
    const scale = this.declutter.markerScale * this.densitySizeMul(e);
    let halfPx: number;
    if (e.kind === "alert") {
      // Mirrors buildAlertGlyphs getSize (the alert glyph replaces the unit frame).
      halfPx =
        (((this.markerMode === "dot" ? 22 : 40) + (this.isEmphasized(e) ? 6 : 0)) / 2) * scale;
    } else if (this.markerMode === "dot") {
      // Mirrors buildMarkers getRadius (the radius IS the half-extent); a hostile renders as
      // the ::dotdiamond icon whose tip half-extent is DOT_DIAMOND_MUL × the circle radius.
      const r =
        ((this.isAlert(e) ? tuning.markerPx + 2.5 : tuning.markerPx) +
          (e.id === this.selectedId ? 2 : 0)) *
        scale;
      halfPx = e.properties.affiliation === "hostile" ? r * DOT_DIAMOND_MUL : r;
    } else {
      // Mirrors buildMilsymMarkers / buildCoalitionMarkers getSize (sprite height; half = /2).
      halfPx = (((this.isAlert(e) ? 40 : 34) + (e.id === this.selectedId ? 6 : 0)) / 2) * scale;
    }
    return Math.round(halfPx + LABEL_GAP_PX);
  }

  /** CALLSIGN LABELS — each track's callsign rendered BENEATH its dot in the AFFILIATION colour.
   *  CHANNEL LAW: the TEXT (the letters) is the information channel; the colour only CONFIRMS the
   *  affiliation, so the label survives the MONO theme (where the affiliation hues collapse to
   *  grey, the callsign is still fully readable). LEGIBILITY: a theme-complementary PROTECTIVE
   *  PLATE behind every label (see plateRgba — near-black on the dark basemaps, white on
   *  light/civic) with a thin 1px SDF rim; plate off (labelPlateAlpha 0) restores the full 2px
   *  outline halo. DECLUTTER: gated on the zoom-driven declutter plan (labels suppressed when
   *  zoomed out so they don't pile into noise) — except an emphasized track always keeps its
   *  label — and a dense surviving knot CLUSTER-LATCHES into one stacked column
   *  (deconflictCallsigns). PRESENTATION ONLY — a label never changes WHAT renders (same
   *  visible `data` as the markers). */
  private buildCallsigns(id: string, data: CanonicalEnvelope[]): Layer {
    // ZOOM-GATE BYPASS (v2 §1.2): a friendly callsign and an ALERT label are ALWAYS shown —
    // never gated away by the zoom declutter (an alert whose glyph shows deserves its label
    // below zoom 7 too) — alongside emphasized tracks and (when not zoomed out) everyone else.
    // Other affiliations still declutter so the resting wide view stays calm.
    const labelled = data.filter(
      (e) =>
        this.callsignOf(e) !== null &&
        labelZoomGateOpen(
          this.declutter.labelsVisible,
          this.isEmphasized(e),
          e.properties.affiliation,
          e.kind,
        ),
    );
    // Offset each label clear of ITS marker's drawn edge — per-entity (labelBelowPx): half the
    // rendered marker extent (mode base × alert/selection bumps × declutter × density-LOD) + gap,
    // so a bigger icon never swallows its label and a shrunken one keeps its label hugged close.
    // CALLSIGN DECLUTTER: a Y-ONLY anti-collision layout over the VISIBLE labels. X stays anchored
    // under the dot; colliding labels nudge Y apart (gradient, not snap). "HIDE, don't squeeze": a
    // label the shrink fallback drove below MIN_LABEL_PX is DROPPED (not drawn tiny), and past
    // LABEL_DENSITY_KEEP on-screen labels the lowest-priority tiers are density-culled — so the tiny-
    // green-vessel wall thins to only what is legible while own-force + emphasized always read.
    // LABEL-presentation only — `labelled` (WHAT the projection renders + picks) is unchanged; only
    // which callsign LABELS draw + each one's pixel-offset Y / font size change (see deckBackend
    // deconflictCallsigns + labelDeconflict.ts). A label is not a track.
    const place = this.deconflictCallsigns(labelled, tuning.callsignBaseSizePx);
    // Draw ONLY the labels that survived viewport-cull + the min-size floor + the density/name cap /
    // drop-at-distance (a label absent from `place` is off-screen or dropped — not rendered). At a
    // normal zoom every on-screen label survives, so this is identity; it only thins a dense pile.
    const drawn = labelled.filter((e) => place.has(e.id));
    // RIGHT-OVER-LEFT PAINT (Will 2026-07-05): deck draws instances in DATA ORDER, so sorting by
    // final anchor screen-X ascending makes the right-most label (and its plate) paint ON TOP
    // where columns/labels overlap — the right column reads clean, the left tucks beneath it.
    drawn.sort((a, b) => (place.get(a.id)?.sortX ?? 0) - (place.get(b.id)?.sortX ?? 0));
    // PROTECTIVE TEXT PLATE (Will: "the halo is faint and distracting") — a translucent
    // theme-complementary plate behind EVERY callsign (latched + solo) is now the PRIMARY
    // legibility device; null = disabled (labelPlateAlpha 0) → the outline returns to its full
    // 2px halo so legibility never regresses when the plate is off.
    const plate = this.plateRgba();
    return new TextLayer<CanonicalEnvelope>({
      id: `${id}::callsign`,
      data: drawn,
      pickable: false,
      getPosition: (e) => this.renderPoint3(e) ?? [0, 0, 0],
      // ROW-CAP CUE: a truncated latch column's carrier renders "…" instead of its callsign. Read
      // from the PER-SPEC `place` (closured into this layer) — NOT a backend-global — so each
      // layer's carriers survive to draw regardless of later specs' rebuilds. frameN getText trigger.
      getText: (e) => (place.get(e.id)?.ellipsis ? "…" : this.callsignOf(e) ?? ""),
      // Affiliation colour CONFIRMS; the text is the channel. Full alpha for legibility (the fade
      // is a MARKER-clutter knob; a faded-to-grey callsign would be unreadable).
      getColor: (e) => this.affRgb(e),
      // DECLUTTER: a piled-up label shrinks so its column packs tighter (labelDeconflict.ts).
      getSize: (e) => place.get(e.id)?.size ?? tuning.callsignBaseSizePx,
      sizeUnits: "pixels",
      // CLUSTER LATCH: a latched label justifies against its stack's shared edge ('start' right
      // of the cluster / 'end' after the right-viewport-edge flip); everything else keeps the
      // classic centred-under-the-dot anchor.
      getTextAnchor: (e) => place.get(e.id)?.anchor ?? "middle",
      getAlignmentBaseline: "top",
      // DECLUTTER: X stays 0 (anchored under the dot) for flow labels — a CLUSTER-LATCHED label
      // instead carries the offset to its stack edge/row. Y carries the per-entity base offset
      // (labelBelowPx) plus the per-label deconfliction nudge (flow) or the stack-row placement
      // (latched).
      getPixelOffset: (e) => {
        const p = place.get(e.id);
        return [p?.offsetX ?? 0, p?.offsetY ?? this.labelBelowPx(e)];
      },
      // SDF font so the outline rim renders crisp at any size (MONO-safe legibility).
      fontFamily: "Menlo, Monaco, Consolas, monospace",
      fontSettings: { sdf: true },
      // THE PLATE is theme-linked: colour = the theme's `label-plate` token (near-black for the
      // dark-basemap families, WHITE for light/civic — chosen per-theme as the COMPLEMENTARY
      // backing under the affiliation-hued text), alpha = tuning.labelPlateAlpha. It re-points on
      // a runtime profile switch via the palette seam (setAffiliationColors → rebuild; the value
      // also rides symbologyKey). Constant accessor — deck diffs the value itself, recolouring
      // plates in place. With the plate on, the SDF outline drops to a supporting 1px rim: it
      // still defines glyph edges (the legibility guarantee for the LIGHT hues — unknown amber /
      // pending orange — on a white plate) without the fat halo Will called distracting.
      background: plate !== null,
      getBackgroundColor: plate ?? [0, 0, 0, 0],
      backgroundPadding: PLATE_PADDING,
      outlineWidth: plate !== null ? 1 : 2,
      outlineColor: [6, 9, 14, 220],
      // A fixed character set so the font atlas is built once; an out-of-set glyph renders BLANK
      // rather than rebuilding the atlas — so the set must cover every glyph a label can carry.
      // Latin-1 accents because the name-fallback labels real vessel names ("Paranaguá Voyager");
      // em/en-dash + comma + semicolon + colon + ellipsis because NWS alert headlines carry them
      // ("Flash Flood Warning — Morgan, OH") and labelText truncates with "…".
      characterSet:
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789 -_/.+#()" +
        "ÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕØÚÙÛÜÝáàâäãåçéèêëíìîïñóòôöõøúùûüý'" +
        "…—–,;:&!?",
      parameters: FLAT_OVERLAY_DEPTH,
      // NO position tween on labels. deck.gl position transitions are ARRAY-INDEX aligned
      // (buffer[i]_old → buffer[i]_new), and this TextLayer's data is a viewport-culled subset
      // that RE-INDEXES whenever the cull membership changes (i.e. on zoom). A tween there makes
      // label[i]'s text glide from the entity that WAS at index i to the one now at index i — the
      // callsign "flying from one sprite to another" mid-zoom, then snapping. Labels must SNAP to
      // their anchor; the per-frame screen-space pixel offset still rides on top for deconfliction.
      transitions: {},
      updateTriggers: {
        getText: this.frameN,
        getColor: this.frameN,
        // Trigger on dataVersion (real position writes), NOT frameN (every zoom/hover/settings
        // rebuild) — so a zoom alone never re-evaluates the anchor and can never reshuffle it.
        getPosition: this.dataVersion,
        // The deconflicted offset + size + latch anchor are screen-space — recompute every
        // rebuild (frameN) and across declutter buckets (declutterKey).
        getPixelOffset: [this.frameN, this.declutterKey],
        getSize: [this.frameN, this.declutterKey],
        getTextAnchor: [this.frameN, this.declutterKey],
      },
    });
  }

  /** Count how many tracks (across every spec) fall inside the current map bounds, stopping the
   *  scan at `cap` (the caller only needs "at most N or more-than-N"). A plain lng/lat bbox test —
   *  no per-point projection — so the sparse-view label override costs ~one float compare per
   *  track even at 100k. Antimeridian-safe (MapLibre's east may exceed 180; lng is normalized up
   *  into [west, west+360)). A headless/stub map (no getBounds) or a non-finite bound returns
   *  Infinity — "unknown viewport" reads as DENSE, so the override never fires on bad data. */
  private countInViewport(cap: number): number {
    let bounds;
    try {
      bounds = this.map.getBounds?.();
    } catch {
      return Number.POSITIVE_INFINITY;
    }
    if (!bounds) return Number.POSITIVE_INFINITY;
    const w = bounds.getWest();
    const e = bounds.getEast();
    const s = bounds.getSouth();
    const n = bounds.getNorth();
    if (![w, e, s, n].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
    let count = 0;
    for (const [, data] of this.frameData) {
      for (const env of data) {
        const p = pointOf(env);
        if (!p) continue;
        const lat = p[1];
        if (lat < s || lat > n) continue;
        let lng = p[0];
        while (lng < w) lng += 360;
        if (lng > e) continue;
        count++;
        if (count >= cap) return count;
      }
    }
    return count;
  }

  /** Run the Y-ONLY callsign deconfliction over the visible labels and return, per id, the
   *  resolved pixel-offset Y (the per-entity base offset labelBelowPx + the nudge) and font
   *  size. Projects each label's
   *  lng/lat to SCREEN pixels (map.project) because the layout is a screen-space anti-collision
   *  pass. Guards a headless / stub / mid-teardown map (project absent or throwing) by falling
   *  back to the base placement for the affected label — never throws into the render loop.
   *  PRESENTATION ONLY — never changes which labels are in `labelled`. */
  private deconflictCallsigns(
    labelled: CanonicalEnvelope[],
    baseSize: number,
  ): Map<string, CallsignPlace> {
    const out = new Map<string, CallsignPlace>();
    const project = this.map.project?.bind(this.map);
    // HEADLESS / stub map: no projection ⇒ base placement for all (never throw into the render loop).
    if (!project || labelled.length === 0) {
      for (const e of labelled) {
        out.set(e.id, {
          offsetX: 0,
          offsetY: this.labelBelowPx(e),
          size: baseSize,
          anchor: "middle",
          sortX: 0,
        });
      }
      return out;
    }
    // VIEWPORT CULL bounds (CSS px, matching project()'s output) with a generous margin so a label
    // near the edge isn't popped mid-pan. Unknown canvas size ⇒ no cull (Infinity bounds).
    const canvas = this.map.getCanvas?.();
    const vw = canvas && Number.isFinite(canvas.clientWidth) ? canvas.clientWidth : Infinity;
    const vh = canvas && Number.isFinite(canvas.clientHeight) ? canvas.clientHeight : Infinity;
    const MARGIN = 80;

    // Build one placement record per ON-SCREEN, drawable label. `always` (friendly OR emphasized/
    // selected/hovered) labels are exempt from BOTH the density cull and the MIN_LABEL_PX floor — an
    // operator must always be able to read own-force + whatever they are pointing at.
    type LabelRec = {
      id: string;
      input: LabelInput;
      always: boolean;
      /** Marker screen position (px) — the CLUSTER-LATCH grouping anchor (input.y carries
       *  +below, so the latch pass keeps the raw projection separately). */
      sx: number;
      sy: number;
      /** The rendered label text (latch stack order key + the width estimate's source). */
      text: string;
      /** This entity's base below-the-marker offset (labelBelowPx) — the collision anchor's
       *  Y component AND the final flow placement's base (kept per-rec so both agree). */
      below: number;
    };
    const recs: LabelRec[] = [];
    for (const e of labelled) {
      const p = pointOf(e);
      if (!p) continue; // no geometry ⇒ not drawable (avoids a null-island [0,0] label)
      let sx: number;
      let sy: number;
      try {
        const scr = project([p[0], p[1]]);
        sx = scr.x;
        sy = scr.y;
      } catch {
        continue; // a projection throw on one point never poisons the frame
      }
      // VIEWPORT CULL: an off-screen label is not drawn (deck culled it anyway), AND this makes the
      // name CAP per-VIEW — so zooming into a dense wide picture reveals the names the cap dropped.
      if (!(sx >= -MARGIN && sx <= vw + MARGIN && sy >= -MARGIN && sy <= vh + MARGIN)) continue;
      const text = this.callsignOf(e) ?? "";
      // ALWAYS-legible set: own-force + the emphasized (selected/hovered) track. Never shrunk, never
      // density-culled, never floor-hidden — they hold full size so they always read.
      const always = this.isFriendly(e) || this.isEmphasized(e);
      // anchor Y = projected screen Y + the PER-ENTITY below-the-marker offset; X stays the
      // dot's X. Per-entity (not the old shared constant) so the collision pass sees each
      // label where it will actually draw — just below ITS marker's rendered edge.
      const below = this.labelBelowPx(e);
      recs.push({
        id: e.id,
        always,
        sx,
        sy,
        text,
        below,
        input: {
          x: sx,
          y: sy + below,
          chars: text.length,
          size: baseSize,
          priority: always, // priority ⇒ exempt from the shrink fallback (friendly + emphasized)
          // SEAM A (v2 §1.1): the ONE global ordinal — emphasized 0, alert 1, friend 2,
          // hostile 3, unknown/pending 4, neutral 5 — feeds the stable-placement sort, the
          // density cull, the latch stack order, and the fast-grid slot priority below. The
          // EXACT pass ignores rank by interface (it never hides labels), which is fine.
          rank: globalRank(e.kind, e.properties.affiliation, this.isEmphasized(e)),
        },
      });
    }
    // STABLE PLACEMENT ORDER (fixes the ~1 Hz label reshuffle). BOTH deconfliction passes break
    // placement ties by INPUT INDEX; the input array used to arrive in the engine snapshot order,
    // which SHIFTS every ~1 Hz age-out reproject (entities crossing the recency edge drop then
    // re-insert at the end of the store Map). That flipped which label won each slot → the whole
    // layout reshuffled once a second even when nothing moved. Sorting the inputs by a FIXED key
    // (globalRank, then the stable entity id) makes the index tiebreak deterministic: identical
    // inputs ⇒ identical placement every rebuild, so the layout holds still between ticks.
    recs.sort((a, b) => {
      const ra = a.input.rank ?? 0;
      const rb = b.input.rank ?? 0;
      if (ra !== rb) return ra - rb;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
    // IDENTICAL-TEXT RADIUS DEDUPE (v2 §1.4 — the "WP-2 ×3" stack AND the "COALITION-BRIEF ×2"
    // echo twin): ONE string per 24px screen neighbourhood per frame. Duplicates reach here two
    // ways — (a) the engine fans a kind-matching entity into MULTIPLE specs (a kind:"track" is
    // in BOTH the tracks and adsb specs), each of whose ::callsign layer draws its own copy;
    // (b) an authored marker that egressed (/emit) returns from the TAK net as a SECOND entity
    // (different id, same name, near-same point — its anchor drifts a few px, which the old
    // exact-pixel slot key missed). Either way the copies carry the SAME text — noise the
    // Y-nudge/latch below would fan out into a visible stacked pile of one string. Runs AFTER
    // the (rank, id) sort (deterministic winner — the first claimant in that order wins every
    // rebuild: the local marker beats the echo) and BEFORE the density cull / latch / nudge
    // passes (a duplicate never inflates a cluster or a collision). The claims registry is
    // per-rebuild and SHARED across specs (rebuildDeck clears it), so the first spec in
    // registration order wins. The REAL fix (gateway origin-id round-trip dedupe) stays booked;
    // this is the render-side mitigation. PRESENTATION ONLY (I13): only a redundant string is
    // skipped — BOTH entities' TRACKS still render and pick.
    const distinct = recs.filter((r) => claimLabelSlot(this.labelClaims, r.text, r.sx, r.sy));
    // PRIORITY-TIERED DENSITY CULL: past LABEL_DENSITY_KEEP on-screen labels the lowest-priority tiers
    // (by rank: neutral, then pending/unknown …) are dropped BEFORE layout so own-force + threats
    // claim the room and the pretty pass is not fed a wall. `always` labels are never dropped. `distinct`
    // is already in rank order, so the survivors are the always-set plus the highest-priority prefix
    // of the cullable set. This also shrinks the near-O(N²) exact pass's input.
    let kept: LabelRec[] = distinct;
    if (distinct.length > tuning.labelDensityKeep) {
      kept = [];
      let budget = tuning.labelDensityKeep;
      for (const r of distinct) {
        if (r.always) {
          kept.push(r); // own-force / emphasized — never density-culled
        } else if (budget > 0) {
          kept.push(r); // highest-priority cullable first (recs is rank-sorted)
          budget--;
        }
        // else: lowest-priority overflow — dropped (its track still renders; only the label is gone)
      }
    }
    // CLUSTER LATCH (label-legibility direction 2026-07-05: "text gets hard to read quickly when
    // clustering over a saturated icon group, even with our spreading algos"). A knot of
    // ≥ tuning.labelClusterLatch SURVIVING labels (the density cull already ran — cull first,
    // latch what survives) stops fighting for per-dot placement and LATCHES into ONE
    // left-justified column beside the group: every member text-anchor 'start' against a shared
    // left edge just right of the group's rightmost marker (flipped to the LEFT side with anchor
    // 'end' at the right viewport edge so the stack never runs off-screen), rows at base+3 pitch
    // centred on the group's Y centroid, ordered rank → callsign → id (globalRank: alerts
    // crown, then friendly; deterministic — the same stable-placement lesson as the recs sort
    // above, so the stack never reshuffles at 1 Hz). Latched labels render at FULL base size (the latch IS the
    // declutter) and are EXCLUDED from the nudge/shrink passes below (already placed);
    // everything else flows through the existing pipeline unchanged. <2 disables (0 = off).
    // Grouping math is pure + T0-pinned (labelDeconflict.ts latchClusters). PRESENTATION ONLY
    // (I13): same labels in, same labels out — only pixel placement changes.
    let flow: LabelRec[] = kept;
    const latchAt = tuning.labelClusterLatch;
    if (latchAt >= 2 && kept.length >= latchAt) {
      const latched = latchClusters(
        kept.map((r) => ({
          id: r.id,
          x: r.sx,
          y: r.sy,
          chars: r.input.chars,
          rank: r.input.rank ?? 0,
          text: r.text,
        })),
        {
          radiusPx: tuning.labelClusterRadiusPx,
          latchAt,
          sizePx: baseSize,
          lineHeightPx: baseSize + LATCH_LINE_GAP_PX,
          marginPx: LATCH_MARGIN_PX,
          viewportW: vw, // Infinity when the canvas is unknown ⇒ never flips (fail-to-default)
          maxRows: tuning.labelClusterMaxRows, // <=0 = unlimited (handled by the pure fn)
        },
      );
      if (latched.placed.length > 0 || latched.hiddenIds.length > 0) {
        const recById = new Map(kept.map((r) => [r.id, r] as const));
        const latchedIds = new Set<string>();
        for (const l of latched.placed) {
          const r = recById.get(l.id);
          if (!r) continue; // defensive — latchClusters only returns ids it was fed
          latchedIds.add(l.id);
          // deck pixel offsets are relative to the label's OWN projected anchor — convert the
          // stack's absolute screen target into this member's offset. sortX = the stack edge:
          // right-most columns sort LAST in the draw array ⇒ paint ON TOP of left columns
          // (Will 2026-07-05 — overlapping adjacent columns resolve right-over-left).
          out.set(l.id, {
            offsetX: l.x - r.sx,
            offsetY: l.y - r.sy,
            size: l.size,
            anchor: l.anchor,
            sortX: l.x,
          });
        }
        // ROW-CAP OVERFLOW: one "…" cue row per truncated column (cap = tuning.labelClusterMaxRows).
        // The carrier is the first OVERFLOW member — getText renders its label as a literal "…"
        // (place.ellipsis, per-spec), a zoom-in cue; the remaining overflow members hide entirely
        // this frame (no place entry ⇒ not drawn; their TRACKS still render — labels only).
        for (const l of latched.ellipses) {
          const r = recById.get(l.id);
          if (!r) continue;
          latchedIds.add(l.id);
          out.set(l.id, {
            offsetX: l.x - r.sx,
            offsetY: l.y - r.sy,
            size: l.size,
            anchor: l.anchor,
            sortX: l.x,
            ellipsis: true,
          });
        }
        for (const id of latched.hiddenIds) latchedIds.add(id);
        flow = kept.filter((r) => !latchedIds.has(r.id));
      }
    }
    const inputs = flow.map((r) => r.input);
    // PLATE-INCLUSIVE COLLISION BOXES (v2 §1.3 — collision honesty): the deconfliction passes
    // must see the TRUE drawn box — glyphs + the protective plate's backgroundPadding — or
    // ~8px of plate-over-text survives "convergence" and right-over-left paint order finishes
    // the smear. [0, 0] when the plate is disabled (labelPlateAlpha 0: bare glyph + outline).
    const platePad: PlatePad = this.plateRgba() !== null ? PLATE_PADDING : [0, 0];
    // TIERING: the pretty gradient + shrink pass for a modest on-screen set; the O(N) fast grid
    // (priority order + name cap + drop-at-distance) once the on-screen pile is too dense for it.
    const placements: LabelPlacement[] =
      inputs.length <= LABEL_DECONFLICT_EXACT_MAX
        ? deconflictLabels(inputs, { platePad })
        : fastDeconflictLabels(inputs, {
            cap: FAST_DECONFLICT.cap,
            maxOffsetRows: FAST_DECONFLICT.maxOffsetRows,
            platePad,
          });
    placements.forEach((pl, i) => {
      if (pl.hidden) return; // fast-path drop / over-cap ⇒ omit (buildCallsigns won't draw it)
      // MIN_LABEL_PX FLOOR — HIDE, don't squeeze: a label the shrink fallback drove below the
      // legibility floor is dropped rather than drawn at an unreadable size. `always` labels are
      // shrink-exempt so they never land here; this thins only the tiny neutral/unknown pile.
      if (!flow[i].always && pl.size < tuning.minLabelPx) return;
      out.set(flow[i].id, {
        offsetX: 0,
        offsetY: flow[i].below + pl.yOffset,
        size: pl.size,
        anchor: "middle",
        sortX: flow[i].sx,
      });
    });
    return out;
  }

  /** HYPERSONIC BLIP — a periodic sensor-style PULSE on very-fast (hypersonic) tracks. The motion
   *  itself is smooth + deterministic (positionAtMs); this adds the blip whose CADENCE scales with
   *  speed (motion.hypersonicBlipPhase — linear, clamped to a max). The pulse is a soft expanding
   *  ring tinted with the track's affiliation hue; at the dark phase of each period it collapses to
   *  nothing. EPOCH-ANCHORED so the pulse is coherent across clients (not a per-session animation).
   *  Built on the engine-driven rebuild AND re-sampled every animation frame by the pump loop
   *  (refreshBlips re-invokes this with a fresh `nowMs` while a hypersonic is on screen), so the
   *  ring breathes at frame rate — smooth, not stepped at the ~1 Hz data cadence. Still NO
   *  independent rAF: the pulse rides the SAME pump that advances the GPU position glide, and it
   *  reads the SAME wall clock (Date.now), so the single-clock, epoch-anchored discipline holds and
   *  the cadence stays deterministic + coherent across clients. */
  private buildBlips(id: string, data: CanonicalEnvelope[], nowMs: number): Layer {
    type Blip = { pos: [number, number, number]; radius: number; color: RGBA };
    const blips: Blip[] = [];
    for (const e of data) {
      const spd = numProp(e, "speed_kn") ?? 0;
      if (spd < HYPERSONIC_MIN_KN) continue; // only genuine hypersonics blip
      const pos = point3Of(e);
      if (!pos) continue;
      const { intensity } = hypersonicBlipPhase(nowMs, spd);
      // ring grows + brightens with the flash envelope; collapses to nothing in the gap.
      const radius = (6 + 16 * intensity) * this.declutter.markerScale;
      const [r, g, b] = this.affRgb(e);
      blips.push({ pos, radius, color: [r, g, b, Math.round(210 * intensity)] });
    }
    return new ScatterplotLayer<Blip>({
      id: `${id}::blip`,
      data: blips,
      pickable: false,
      stroked: true,
      filled: false,
      radiusUnits: "pixels",
      lineWidthUnits: "pixels",
      getLineWidth: 2,
      getPosition: (d) => d.pos,
      getRadius: (d) => d.radius,
      getLineColor: (d) => d.color,
      parameters: FLAT_OVERLAY_DEPTH,
      // The blip `data` array is rebuilt (with the pulse radius baked in) every rebuild AND every
      // pump frame (refreshBlips) — a fresh data ref re-runs these accessors regardless, so the
      // ring breathes smoothly at frame rate. frameN keeps the triggers honest for the data-cadence
      // rebuild path too.
      updateTriggers: {
        getRadius: this.frameN,
        getLineColor: this.frameN,
        getPosition: this.frameN,
      },
    });
  }
}
